import * as THREE from 'three';
import type { PackModel } from '../../render/models/modelLibrary';
import { emitStreetModel } from '../../render/models/streetModels';
import type { BuildingLot } from '../types';
import { ChunkedBuilders, hash01, type MeshBuilder, type RGB } from './meshBuilder';
import { CLASSIC_BUILDINGS, CLAY_MARK } from './buildingDetail';
import { T, tileUV, type SignAtlas, type SignStyle } from './textures';

/**
 * Procedural period architecture. Each BuildingLot becomes merged geometry in the `facade` builder
 * of its chunk (walls, roofs, chimneys, bays, trims all share the facade atlas — trims use the white
 * tile + vertex colour), plus signage quads in the global sign builders and emissive bits in `glow`.
 *
 * Lot frame: local +Z is the front (faces the street), local +X runs along the frontage.
 */

type V3 = [number, number, number];

interface Skin {
  plain: number;
  win: number[];
  lit: number[];
  door: number;
}

const SKIN = {
  brick: { plain: T.brickPlain, win: [T.brickWinDark, T.brickWinDark2, T.brickWinNets], lit: [T.brickWinLit, T.brickWinLit2, T.brickWinTV, T.brickWinDim], door: T.brickDoor },
  soot: { plain: T.brickSoot, win: [T.brickWinDark, T.brickWinDark2], lit: [T.brickWinLit, T.brickWinLit2, T.brickWinDim], door: T.brickDoor },
  /** Yellow-grey London stock brick with gauged red arches. */
  stock: { plain: T.stockPlain, win: [T.stockWinDark, T.stockWinNets], lit: [T.stockWinLit, T.stockWinLit2], door: T.stockDoor },
  stucco: { plain: T.stuccoPlain, win: [T.stuccoWinDark], lit: [T.stuccoWinLit], door: T.stuccoDoor },
  stone: { plain: T.stonePlain, win: [T.stoneWinDark], lit: [T.stoneWinLit], door: T.stoneDoor },
  concrete: { plain: T.concretePlain, win: [T.flatsWinDark], lit: [T.flatsWinLit], door: T.rollerDoor },
  warehouse: { plain: T.brickSoot, win: [T.brickArchWinDark], lit: [T.brickArchWinLit], door: T.loadingDoor },
  factory: { plain: T.brickSoot, win: [T.factoryWinDark], lit: [T.factoryWinLit], door: T.rollerDoor },
  corrugated: { plain: T.corrugated, win: [T.corrugated], lit: [T.corrugated], door: T.rollerDoor },
} satisfies Record<string, Skin>;

const WHITE = tileUV(T.white, 40);
const UP: V3 = [0, 1, 0];
const C = {
  cream: [0.86, 0.82, 0.72] as RGB,
  stone: [0.66, 0.64, 0.58] as RGB,
  coping: [0.3, 0.29, 0.28] as RGB,
  cornice: [0.4, 0.39, 0.36] as RGB,
  dark: [0.09, 0.09, 0.1] as RGB,
  bottle: [0.1, 0.22, 0.16] as RGB,
  oxblood: [0.3, 0.09, 0.07] as RGB,
  navy: [0.09, 0.13, 0.24] as RGB,
  pot: [0.55, 0.3, 0.2] as RGB,
  chimney: [0.42, 0.24, 0.18] as RGB,
  ridge: [0.16, 0.16, 0.18] as RGB,
  clayRidge: [0.38, 0.17, 0.11] as RGB,
  lead: [0.34, 0.36, 0.38] as RGB,
  metal: [0.25, 0.27, 0.28] as RGB,
  gold: [0.75, 0.58, 0.25] as RGB,
};
/**
 * Baked contact shading (docs/POLISH.md, "Graphics batch 2"): local occlusion written into the vertex
 * colours as soft vertical gradients on the vertices that are already there. Not sunlight: the foot of
 * every wall where it meets the pavement, the wall top under projecting eaves and cornices, shop glass
 * under the fascia and awning, door recesses, and anything standing on a roof. Factors are the colour
 * at the darkest vertex; LOW (no shadow maps) leans on it most.
 */
const AO = {
  /** Wall / box foot at ground level, easing out over FOOT_H. */
  foot: 0.72,
  /** Wall top under an eave overhang or cornice. */
  eave: 0.84,
  /** Shop / pub glass top under the fascia board, deeper under an awning. */
  fascia: 0.82,
  awning: 0.76,
  /** Door recess (top and bottom). */
  recess: 0.84,
  /** Foot of things standing on a roof: parapet inner faces, roof clutter, dormers. */
  roof: 0.74,
  /** Chimney stacks where they come through the slates. */
  chimney: 0.62,
};
const FOOT_H = 1.2;
const footShade = (y: number): number => {
  if (y >= FOOT_H) return 1;
  const t = Math.max(0, y) / FOOT_H;
  return 1 - (1 - AO.foot) * (1 - t * t * (3 - 2 * t));
};
const mulRGB = (c: RGB, k: number): RGB => (k === 1 ? c : [c[0] * k, c[1] * k, c[2] * k]);
/** Multiply the colours of vertices [first, first + n) of `b` by `k`. */
function darkenVerts(b: MeshBuilder, first: number, n: number, k: number): void {
  for (let i = first; i < first + n; i++) {
    b.col[i * 3] *= k;
    b.col[i * 3 + 1] *= k;
    b.col[i * 3 + 2] *= k;
  }
}

const DOOR_COLOURS: RGB[] = [
  [0.45, 0.08, 0.07],
  [0.08, 0.22, 0.14],
  [0.08, 0.14, 0.32],
  [0.08, 0.08, 0.08],
  [0.75, 0.6, 0.18],
  [0.85, 0.83, 0.78],
  [0.3, 0.18, 0.4],
];
/** The council's front-door colours for the deck-access blocks: blue, green, red and mustard, no purple or off-white. */
const COUNCIL_DOORS: RGB[] = [
  [0.1, 0.25, 0.5],
  [0.1, 0.35, 0.25],
  [0.6, 0.12, 0.1],
  [0.75, 0.6, 0.15],
];

/** What a shopfront sells, read from its sign (visual only: picks window dressing and street clutter). */
type ShopKind = 'launderette' | 'bookie' | 'cafe' | 'pawn' | 'produce' | 'news' | 'hardware' | 'plain';
function shopKind(name: string | undefined, cafe: boolean): ShopKind {
  const n = (name ?? '').toLowerCase();
  if (n.includes('launderette')) return 'launderette';
  if (n.includes('turf')) return 'bookie';
  if (cafe || /caf|tea room|dining|snack|grill|fish bar/.test(n)) return 'cafe';
  if (n.includes('pawn')) return 'pawn';
  if (/greengrocer|butcher|wet fish|bakery/.test(n)) return 'produce';
  if (/newsagent|tobacconist/.test(n)) return 'news';
  if (/hardware|ironmonger|cycle/.test(n)) return 'hardware';
  return 'plain';
}
const NEWS_BILLS = ['EVENING POST · DOCK STRIKE LATEST', 'KESSMOUTH GAZETTE · BANK RAID: POLICE BAFFLED', 'EVENING POST · GULLET BODY MYSTERY', 'KESSMOUTH GAZETTE · FOG CHAOS ON RIVER'];

export interface BuildingSinks {
  cb: ChunkedBuilders;
  signs: SignAtlas;
  /** Meshy roof kit (street pack, docs/research/MESHY-WORLD-PLAN.md Tier 2), when it loaded. */
  roofKit?: RoofKit | null;
}

/**
 * The Meshy roof kit: vertex-baked models stretched to the procedural piece's box, one for one (never more
 * instances than the procedural roofs have). Chimney stacks go in `facadeModel` (MEDIUM / HIGH) with their
 * procedural shaft as the `facadeLow` stand-in; the water tank and the cowl replace `facadeMid` detail, which
 * LOW never drew.
 */
export interface RoofKit {
  /** A party-wall stack with a row of pots (long along its model x). */
  chimneyA: PackModel | null;
  /** A narrow stack with two pots. */
  chimneyB: PackModel | null;
  waterTank: PackModel | null;
  /** Mushroom ventilator cowl on its flashing plate. */
  vent: PackModel | null;
  /** Roof kit v2 (art/models/blender/export_roof_kit.py): soil pipe with a cowl, glazed lantern, Yagi aerial, lead
   * dormer (the sash tile goes over its opening), tank / lift housing (walls tinted by the lot). Same builders
   * as the boxes they replace: `facadeMid`, the aerial `facadeFine`. */
  ventStack: PackModel | null;
  skylight: PackModel | null;
  aerial: PackModel | null;
  dormer: PackModel | null;
  house: PackModel | null;
}

/** World heading every TV aerial's boom points along: the one transmitter the whole borough is tuned to. */
const AERIAL_HEADING = -0.55;

const _km = new THREE.Matrix4();
const _kq = new THREE.Quaternion();
const _kp = new THREE.Vector3();
const _ks = new THREE.Vector3();
const _ky = new THREE.Vector3(0, 1, 0);
/** The facade atlas's white tile (the baked models' colour is all in the vertices). */
const WHITE_UV = ((): [number, number] => {
  const t = tileUV(T.white, 40);
  return [(t[0] + t[2]) / 2, (t[1] + t[3]) / 2];
})();

/** Local frame helper for a lot. */
class Frame {
  readonly cs: number;
  readonly sn: number;
  constructor(
    readonly cx: number,
    readonly cz: number,
    angle: number,
  ) {
    this.cs = Math.cos(angle);
    this.sn = Math.sin(angle);
  }
  p(lx: number, y: number, lz: number): V3 {
    return [this.cx + lx * this.cs + lz * this.sn, y, this.cz - lx * this.sn + lz * this.cs];
  }
  /** Local direction -> world direction. */
  d(lx: number, ly: number, lz: number): V3 {
    return [lx * this.cs + lz * this.sn, ly, -lx * this.sn + lz * this.cs];
  }
}

export function buildBuildings(lots: BuildingLot[], sinks: BuildingSinks): void {
  for (const lot of lots) {
    if (lot.style === 'arches' || lot.style === 'gasholder') continue;
    const fb = sinks.cb.get('facade', lot.center.x, lot.center.z);
    const glow = sinks.cb.get('glow', lot.center.x, lot.center.z);
    const ctx = new LotBuilder(lot, fb, glow, sinks);
    ctx.build();
    ctx.shadeEaves();
  }
}

class LotBuilder {
  readonly f: Frame;
  readonly W: number;
  readonly D: number;
  readonly H: number;
  readonly id: number;
  /** Detail drawn on HIGH and MEDIUM (cityView appends it to the chunk's facade geometry). */
  readonly mid: MeshBuilder;
  /** Detail drawn on HIGH only. */
  readonly fine: MeshBuilder;
  /** Lots outside the playable boundary: the big shapes only (no pots, sills, pipes or dormers). */
  readonly backdrop: boolean;
  private salt = 0;
  /** Wall-top vertices at the eaves line (index of the first of each pair in `b`), by face. */
  private eaveVerts: { front: number[]; back: number[]; side: number[] } = { front: [], back: [], side: [] };
  /** Faces with something projecting over their top (set by the roof builders). */
  private eaveFaces = new Set<'front' | 'back' | 'side'>();
  /** Height of the shop fascia (shopfront / pub), whose ground-floor glass sits in its shade. */
  private fasciaY = -1;

  constructor(
    readonly lot: BuildingLot,
    readonly b: MeshBuilder,
    readonly glow: MeshBuilder,
    readonly sinks: BuildingSinks,
  ) {
    this.f = new Frame(lot.center.x, lot.center.z, lot.angle);
    this.W = lot.halfX * 2;
    this.D = lot.halfZ * 2;
    this.H = lot.height;
    this.id = lot.id;
    this.backdrop = lot.districtId === 'backdrop';
    this.mid = sinks.cb.get('facadeMid', lot.center.x, lot.center.z);
    this.fine = sinks.cb.get('facadeFine', lot.center.x, lot.center.z);
  }

  rnd(): number {
    return hash01(this.id, this.salt++, 17);
  }
  has(tag: string): boolean {
    return this.lot.tags.includes(tag);
  }
  tagValue(prefix: string): string | null {
    const t = this.lot.tags.find((x) => x.startsWith(prefix));
    return t ? t.slice(prefix.length) : null;
  }

  /** Lot tint: subtle per-building variation on the atlas colours. */
  tint(strength = 1): RGB {
    const r = hash01(this.id, 3);
    const g = hash01(this.id, 5);
    const v = 0.82 + r * 0.22;
    const warm = (g - 0.5) * 0.12 * strength;
    return [v + warm, v, v - warm];
  }

  // ------------------------------------------------------------------ primitives in local space

  quadL(a: [number, number], bpt: [number, number], y0: number, y1: number, tile: number, c: RGB, out = 0, b: MeshBuilder = this.b, topShade = 1): void {
    // vertical quad from local a to b (outward normal = (b-a) x up), pushed out by `out`
    const dx = bpt[0] - a[0];
    const dz = bpt[1] - a[1];
    const l = Math.hypot(dx, dz) || 1;
    const nx = -dz / l;
    const nz = dx / l;
    const ax = a[0] + nx * out;
    const az = a[1] + nz * out;
    const bx = bpt[0] + nx * out;
    const bz = bpt[1] + nz * out;
    // contact shading: the foot of the wall darkens towards the pavement (and the top by `topShade`)
    b.quad(this.f.p(ax, y0, az), this.f.p(bx, y0, bz), this.f.p(bx, y1, bz), this.f.p(ax, y1, az), tile === T.white ? WHITE : tileUV(tile), mulRGB(c, footShade(y0)), mulRGB(c, footShade(y1) * topShade));
    if (b === this.b && out === 0 && Math.abs(y1 - this.H) < 0.05) {
      const face = Math.abs(a[1] - bpt[1]) < 1e-3 ? (a[1] > 0 ? 'front' : 'back') : 'side';
      this.eaveVerts[face].push(b.vertexCount - 2);
    }
  }

  /** After the lot is built: darken the wall tops that ended up under an eave or cornice. */
  shadeEaves(): void {
    for (const f of this.eaveFaces) for (const i of this.eaveVerts[f]) darkenVerts(this.b, i, 2, AO.eave);
  }

  /**
   * A ledge standing proud of the wall plane a->b (outward = (b-a) x up): string courses, cornices,
   * sills, gutters and brackets are all this box. `width` defaults to the whole wall, `offset` moves
   * its centre along the wall. The box starts 3 cm inside the wall so no gap shows at the joint.
   */
  ledge(a: [number, number], bpt: [number, number], y: number, h: number, out: number, c: RGB, b: MeshBuilder, opts: { width?: number; offset?: number } = {}): void {
    const dx = bpt[0] - a[0];
    const dz = bpt[1] - a[1];
    const l = Math.hypot(dx, dz) || 1;
    const ux = dx / l;
    const uz = dz / l;
    const nx = -uz;
    const nz = ux;
    const w = opts.width ?? l;
    const t = opts.offset ?? 0;
    const sz = out + 0.03;
    const cx = (a[0] + bpt[0]) / 2 + ux * t + nx * (sz / 2 - 0.03);
    const cz = (a[1] + bpt[1]) / 2 + uz * t + nz * (sz / 2 - 0.03);
    const p = this.f.p(cx, y, cz);
    b.box(p[0], p[1], p[2], w, h, sz, this.lot.angle + Math.atan2(-uz, ux), c, WHITE);
  }

  /** Is `id` one of this skin's window tiles (something that wants a sill)? */
  isWindow(sk: Skin, id: number): boolean {
    return sk.win.includes(id) || sk.lit.includes(id) || id === T.brickWinBoarded;
  }

  /**
   * Grid of cells along a wall; tile(col,row) returns a tile id or -1 to skip. `sills` adds a
   * projecting stone sill under every window cell of `sillSkin` (HIGH only; never on backdrop lots).
   */
  wallGrid(a: [number, number], bpt: [number, number], rows: number[], cols: number, tile: (col: number, row: number) => number, c: RGB, sillSkin?: Skin): void {
    let y = 0;
    const len = Math.hypot(bpt[0] - a[0], bpt[1] - a[1]);
    const cellW = len / cols;
    const sillC: RGB = sillSkin === SKIN.stucco ? [0.8, 0.78, 0.72] : C.stone;
    for (let r = 0; r < rows.length; r++) {
      const h = rows[r]!;
      for (let k = 0; k < cols; k++) {
        const t0 = k / cols;
        const t1 = (k + 1) / cols;
        const p0: [number, number] = [a[0] + (bpt[0] - a[0]) * t0, a[1] + (bpt[1] - a[1]) * t0];
        const p1: [number, number] = [a[0] + (bpt[0] - a[0]) * t1, a[1] + (bpt[1] - a[1]) * t1];
        const id = tile(k, r);
        const under = r === 0 && this.fasciaY > 0 && Math.abs(y + h - this.fasciaY) < 0.05 ? (this.has('awning') ? AO.awning : AO.fascia) : 1;
        if (id >= 0) this.quadL(p0, p1, y, y + h, id, c, 0, this.b, under);
        // the sash tile's sill sits 21 % up the cell; the stone projects 10 cm
        if (sillSkin && !this.backdrop && id >= 0 && this.isWindow(sillSkin, id)) this.ledge(a, bpt, y + h * 0.21 - 0.05, 0.07, 0.1, sillC, this.fine, { width: cellW * 0.44, offset: (k + 0.5) * cellW - len / 2 });
      }
      y += h;
    }
  }

  /** String courses (a stone band at each floor line) across the front, and the open sides. */
  stringCourses(rows: number[], sk: Skin, faces: 'front' | 'all' = 'front'): void {
    if (this.backdrop || rows.length < 2) return;
    if (sk === SKIN.concrete || sk === SKIN.corrugated || sk === SKIN.factory || sk === SKIN.warehouse) return;
    const W = this.W;
    const D = this.D;
    const c: RGB = sk === SKIN.stucco ? [0.8, 0.78, 0.72] : sk === SKIN.stone ? [0.58, 0.56, 0.5] : C.stone;
    let y = 0;
    for (let r = 0; r < rows.length - 1; r++) {
      y += rows[r]!;
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], y - 0.07, 0.14, 0.1, c, this.mid);
      if (faces === 'all' || this.has('open-left')) this.ledge([W / 2, D / 2], [W / 2, -D / 2], y - 0.07, 0.14, 0.1, c, this.mid);
      if (faces === 'all' || this.has('open-right')) this.ledge([-W / 2, -D / 2], [-W / 2, D / 2], y - 0.07, 0.14, 0.1, c, this.mid);
    }
  }

  /** Cast-iron gutter along the front eaves and a downpipe every ~6 m (pipes HIGH only). */
  eavesGutter(y: number, pipes = true): void {
    if (y === this.H) this.eaveFaces.add('front');
    if (this.backdrop) return;
    const W = this.W;
    const D = this.D;
    this.ledge([-W / 2, D / 2], [W / 2, D / 2], y - 0.16, 0.14, 0.18, C.dark, this.mid);
    if (!pipes) return;
    const n = Math.max(1, Math.round(W / 6.5));
    for (let i = 0; i <= n; i++) {
      const x = Math.max(-W / 2 + 0.15, Math.min(W / 2 - 0.15, -W / 2 + (W * i) / n));
      this.boxL(x, 0.1, D / 2 + 0.08, 0.1, y - 0.3, 0.1, C.dark, T.white, undefined, this.fine);
    }
  }

  /**
   * A dormer on the front (side 1) or rear (-1) slope of a ridge-along-X roof at local x `u`:
   * lead-clad cheeks, a sash in the face, a flat lead top. Drawn on HIGH and MEDIUM.
   */
  dormer(u: number, pitch: number, sk: Skin, side: 1 | -1 = 1, lit = false): void {
    if (this.backdrop) return;
    const span = this.D;
    const rise = (span / 2) * Math.tan(pitch);
    const s0 = 0.22;
    const zf = side * (span / 2) * (1 - s0);
    const yb = this.H + rise * s0 - 0.3;
    const w = 1.3;
    const hh = 1.5;
    const d = 1.15;
    const zc = zf - (side * d) / 2;
    const kit = this.sinks.roofKit;
    if (kit?.dormer) {
      // the roof-kit dormer at its own size: its face is 0.565 m in front of its box centre (the lead roof
      // overhangs it, the back is in the slope); the sash tile below goes over the model's dark opening
      this.kitModel(kit.dormer, this.mid, u, yb, zf - side * 0.565, 1.46, 1.58, 1.27, side > 0 ? 0 : Math.PI);
    } else {
      this.boxL(u, yb, zc, w, hh, d, C.lead, T.lead, T.lead, this.mid, AO.roof);
      this.boxL(u, yb + hh, zc + side * 0.05, w + 0.16, 0.08, d + 0.1, [0.3, 0.32, 0.34], T.white, undefined, this.mid);
    }
    const win = lit ? sk.lit[0]! : sk.win[0]!;
    const z = zf + side * 0.012;
    if (side > 0) this.quadL([u - w / 2 + 0.06, z], [u + w / 2 - 0.06, z], yb + 0.12, yb + hh - 0.1, win, this.tint(), 0, this.mid);
    else this.quadL([u + w / 2 - 0.06, z], [u - w / 2 + 0.06, z], yb + 0.12, yb + hh - 0.1, win, this.tint(), 0, this.mid);
  }

  /** `shade`: contact shading at the foot of the sides (default: the ground-foot gradient when it stands on the ground). */
  boxL(lx: number, y: number, lz: number, sx: number, sy: number, sz: number, c: RGB, tile: number = T.white, topTile?: number, b: MeshBuilder = this.b, shade?: number): void {
    const p = this.f.p(lx, y, lz);
    const uv = tile === T.white ? WHITE : tileUV(tile);
    const baseShade = shade ?? (y < 0.01 ? footShade(0) : 1);
    b.box(p[0], p[1], p[2], sx, sy, sz, this.lot.angle, c, uv, { ...(topTile !== undefined ? { uvTop: topTile === T.white ? WHITE : tileUV(topTile) } : {}), baseShade });
  }

  /** Sign quad on a wall plane facing local +Z at lz, centred at lx, bottom at y. */
  signL(text: string, style: SignStyle, lx: number, y: number, lz: number, width: number, lit: boolean, faceAngle = 0): void {
    const s = this.sinks.signs.add(text, style);
    const h = width / s.aspect;
    const b = this.sinks.cb.global(lit ? 'litSigns' : 'signs');
    const ca = Math.cos(faceAngle);
    const sa = Math.sin(faceAngle);
    // local horizontal direction of the sign (perpendicular to its facing)
    const ux = ca;
    const uz = -sa;
    const fx = sa * 0.02;
    const fz = ca * 0.02;
    const p0 = this.f.p(lx - (ux * width) / 2 + fx, y, lz - (uz * width) / 2 + fz);
    const p1 = this.f.p(lx + (ux * width) / 2 + fx, y, lz + (uz * width) / 2 + fz);
    const p2 = this.f.p(lx + (ux * width) / 2 + fx, y + h, lz + (uz * width) / 2 + fz);
    const p3 = this.f.p(lx - (ux * width) / 2 + fx, y + h, lz - (uz * width) / 2 + fz);
    b.quad(p0, p1, p2, p3, s.uv, [1, 1, 1]);
  }

  glowBox(lx: number, y: number, lz: number, sx: number, sy: number, sz: number, c: RGB): void {
    this.boxL(lx, y, lz, sx, sy, sz, c, T.white, undefined, this.glow);
  }

  // ------------------------------------------------------------------ roofs

  /** Pitched roof over the footprint. axis 'x' = ridge along local X. */
  gableRoof(y: number, pitch: number, axis: 'x' | 'z', roofTile: number = T.slate, gableTile: number = T.brickPlain, overhang = 0.3, roofTint?: RGB): number {
    const W = this.W;
    const D = this.D;
    const span = axis === 'x' ? D : W;
    const length = axis === 'x' ? W : D;
    const rise = (span / 2) * Math.tan(pitch);
    if (y === this.H && overhang > 0) {
      if (axis === 'x') this.eaveFaces.add('front').add('back');
      else this.eaveFaces.add('side');
    }
    // ridge tiles: dark blue-black on most, red clay on some streets; a clay ridge now means a clay-tiled roof
    // (the detail pass paints it; the CLAY_MARK tint is the tag, see buildingDetail.ts)
    const clay = roofTile === T.slate && hash01(this.id, 27) < 0.4;
    const clayRoof = clay && hash01(this.id, 28) < 0.65 && !CLASSIC_BUILDINGS && !this.backdrop;
    const tint = roofTint ?? this.roofTint();
    const gt = this.tint();
    const cells = Math.max(1, Math.round(length / 4));
    const slopeRows = Math.max(1, Math.round(span / 2 / 3.5));
    const L = (u: number, v: number, yy: number): V3 => (axis === 'x' ? this.f.p(u, yy, v) : this.f.p(v, yy, u));
    for (const side of [1, -1]) {
      for (let k = 0; k < cells; k++) {
        const u0 = -length / 2 + (length * k) / cells;
        const u1 = -length / 2 + (length * (k + 1)) / cells;
        for (let r = 0; r < slopeRows; r++) {
          const s0 = r / slopeRows;
          const s1 = (r + 1) / slopeRows;
          // from eaves (s=0) to ridge (s=1)
          const v0 = side * (span / 2 + overhang) * (1 - s0);
          const v1 = side * (span / 2 + overhang) * (1 - s1);
          const y0 = y + rise * s0 - overhang * Math.tan(pitch) * (1 - s0);
          const y1 = y + rise * s1 - overhang * Math.tan(pitch) * (1 - s1);
          const a = L(u0, v0, y0);
          const bb = L(u1, v0, y0);
          const c = L(u1, v1, y1);
          const d = L(u0, v1, y1);
          // per-run variation: patched and re-slated sections read as separate houses from the air
          const vr = 0.88 + hash01(this.id, k * 2 + (side > 0 ? 0 : 1), 23) * 0.22;
          const hr = hash01(this.id, k * 2 + (side > 0 ? 0 : 1), 29);
          const blue = roofTile === T.slate && hr < 0.15 ? 1.08 : 1;
          // lichen / moss on the odd north-facing run
          const moss = clayRoof ? CLAY_MARK : roofTile === T.slate && hr > 0.93 ? [0.95, 1.04, 0.82] : [1, 1, 1];
          this.b.quadFacing(a, bb, c, d, tileUV(roofTile), [tint[0] * vr * moss[0]!, tint[1] * vr * moss[1]!, tint[2] * vr * (clayRoof ? 1 : blue) * moss[2]!], UP);
        }
      }
    }
    // gable triangles
    for (const end of [-1, 1]) {
      const u = (end * length) / 2;
      const p0 = L(u, -span / 2, y);
      const p1 = L(u, span / 2, y);
      const p2 = L(u, 0, y + rise);
      const out = axis === 'x' ? this.f.d(end, 0, 0) : this.f.d(0, 0, end);
      this.b.triFacing(p0, p1, p2, tileUV(gableTile), gt, out);
    }
    // ridge cap
    const rc = axis === 'x' ? [0, y + rise - 0.05, 0, length + 0.2, 0.18, 0.28] : [0, y + rise - 0.05, 0, 0.28, 0.18, length + 0.2];
    const ridgeC = clay ? C.clayRidge : C.ridge;
    this.boxL(rc[0]!, rc[1]!, rc[2]!, rc[3]!, rc[4]!, rc[5]!, ridgeC);
    // the ridge is laid in half-round tiles: alternating tones along the top so it reads as a run of
    // tiles from above, not a bar (HIGH only; a backdrop roof is too far away)
    if (!this.backdrop && roofTile === T.slate) {
      const yt = y + rise + 0.135;
      const step = 0.6;
      let k = 0;
      for (let u = -length / 2; u < length / 2; u += step, k++) {
        const u1 = Math.min(u + step, length / 2);
        const tone = k % 2 ? 0.86 : 1.08;
        const tc: RGB = [ridgeC[0] * tone, ridgeC[1] * tone, ridgeC[2] * tone];
        const q = axis === 'x' ? [this.f.p(u, yt, 0.15), this.f.p(u1, yt, 0.15), this.f.p(u1, yt, -0.15), this.f.p(u, yt, -0.15)] : [this.f.p(-0.15, yt, u), this.f.p(0.15, yt, u), this.f.p(0.15, yt, u1), this.f.p(-0.15, yt, u1)];
        this.fine.quadFacing(q[0]!, q[1]!, q[2]!, q[3]!, WHITE, tc, UP);
      }
    }
    return rise;
  }

  /**
   * Flat patch lying on a roof slope of a gable roof (from `gableRoof(this.H, pitch, axis)`):
   * local u0..u1 along the ridge, s0..s1 from eaves (0) to ridge (1), side +1 = front slope.
   */
  slopePatch(u0: number, u1: number, s0: number, s1: number, side: 1 | -1, pitch: number, tile: number, c: RGB, b: MeshBuilder = this.b, axis: 'x' | 'z' = 'x'): void {
    const span = axis === 'x' ? this.D : this.W;
    const rise = (span / 2) * Math.tan(pitch);
    const P = (u: number, sv: number): V3 => {
      const yy = this.H + rise * sv + 0.06;
      const v = side * (span / 2) * (1 - sv);
      return axis === 'x' ? this.f.p(u, yy, v) : this.f.p(v, yy, u);
    };
    b.quadFacing(P(u0, s0), P(u1, s0), P(u1, s1), P(u0, s1), tile === T.white ? WHITE : tileUV(tile), c, UP);
  }

  /** Slate varies by quarry: blue-grey on most roofs, a purple-grey or green-grey run on some. */
  roofTint(): RGB {
    const v = 0.8 + hash01(this.id, 9) * 0.3;
    const q = hash01(this.id, 10);
    if (q < 0.18) return [v * 1.04, v * 0.97, v * 1.0];
    if (q < 0.3) return [v * 0.95, v * 1.02, v * 0.94];
    return [v, v, v * 1.03];
  }

  /**
   * Flat roof: felt in 5 m sheets a shade apart, standing water where it sags (HIGH), a brick parapet
   * with a stone coping, and a projecting cornice under the front parapet when `cornice` is set.
   */
  flatRoof(y: number, parapet = 0.9, tile: number = T.felt, coping: RGB = C.coping, clutter = true, cornice = false): void {
    const W = this.W;
    const D = this.D;
    const nx = Math.max(1, Math.round(W / 5));
    const nz = Math.max(1, Math.round(D / 5));
    const t = this.roofTint();
    for (let i = 0; i < nx; i++) {
      for (let k = 0; k < nz; k++) {
        const x0 = -W / 2 + (W * i) / nx;
        const x1 = -W / 2 + (W * (i + 1)) / nx;
        const z0 = -D / 2 + (D * k) / nz;
        const z1 = -D / 2 + (D * (k + 1)) / nz;
        const vr = 0.96 + hash01(this.id, i * 7 + k, 33) * 0.08;
        this.b.quadFacing(this.f.p(x0, y, z1), this.f.p(x1, y, z1), this.f.p(x1, y, z0), this.f.p(x0, y, z0), tileUV(tile), [t[0] * vr, t[1] * vr, t[2] * vr], UP);
      }
    }
    if (!this.backdrop && tile === T.felt) {
      // ponding: one or two dark sheets of standing water that mirror the sky a little
      const n = 1 + Math.floor(hash01(this.id, 34) * 2);
      for (let i = 0; i < n; i++) {
        const px = (hash01(this.id, i, 35) - 0.5) * (W - 3);
        const pz = (hash01(this.id, i, 36) - 0.5) * (D - 3);
        const rx = 0.8 + hash01(this.id, i, 37) * 1.6;
        const rz = 0.6 + hash01(this.id, i, 38) * 1.2;
        this.fine.quadFacing(this.f.p(px - rx, y + 0.015, pz + rz), this.f.p(px + rx, y + 0.015, pz + rz), this.f.p(px + rx, y + 0.015, pz - rz), this.f.p(px - rx, y + 0.015, pz - rz), tileUV(tile), [t[0] * 0.5, t[1] * 0.52, t[2] * 0.58], UP);
      }
    }
    if (cornice && y === this.H) this.eaveFaces.add('front');
    if (cornice && !this.backdrop) {
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], y - 0.42, 0.2, 0.28, C.cornice, this.mid);
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], y - 0.22, 0.14, 0.42, C.cornice, this.mid);
    }
    if (parapet > 0) {
      const th = 0.32;
      const wt = this.tint();
      // the roof-side face of each parapet darkens where it meets the felt (box sides are emitted
      // +Z, -Z, +X, -X; the first two vertices of each side are its foot); the street face stays clean
      const parapetBox = (lx: number, lz: number, sx: number, sz: number, inner: number): void => {
        const v0 = this.b.vertexCount;
        this.boxL(lx, y, lz, sx, parapet, sz, wt, this.skinPlain(), this.skinPlain(), this.b, 1);
        darkenVerts(this.b, v0 + inner * 4, 2, AO.roof);
      };
      parapetBox(0, D / 2 - th / 2, W, th, 1);
      parapetBox(0, -D / 2 + th / 2, W, th, 0);
      parapetBox(W / 2 - th / 2, 0, th, D - th * 2, 3);
      parapetBox(-W / 2 + th / 2, 0, th, D - th * 2, 2);
      // coping
      this.boxL(0, y + parapet, D / 2 - th / 2, W + 0.1, 0.12, th + 0.1, coping);
      this.boxL(0, y + parapet, -D / 2 + th / 2, W + 0.1, 0.12, th + 0.1, coping);
      this.boxL(W / 2 - th / 2, y + parapet, 0, th + 0.1, 0.12, D, coping);
      this.boxL(-W / 2 + th / 2, y + parapet, 0, th + 0.1, 0.12, D, coping);
    }
    if (clutter) this.roofClutter(y);
  }

  /** Dentil course under a cornice (bank, office): a small block every 45 cm, HIGH only. */
  dentils(y: number): void {
    if (this.backdrop) return;
    const W = this.W;
    const D = this.D;
    for (let x = -W / 2 + 0.4; x < W / 2 - 0.3; x += 0.45) this.boxL(x, y, D / 2 + 0.08, 0.18, 0.16, 0.2, C.cornice, T.white, undefined, this.fine);
  }

  roofClutter(y: number): void {
    if (this.backdrop) return;
    // the detail pass gives flat roofs a little more on them (they fill much of the top-down frame)
    const n = CLASSIC_BUILDINGS ? Math.min(4, Math.floor((this.W * this.D) / 120)) : Math.min(6, Math.floor((this.W * this.D) / 75));
    for (let i = 0; i < n; i++) {
      const lx = (this.rnd() - 0.5) * (this.W - 4);
      const lz = (this.rnd() - 0.5) * (this.D - 4);
      const kind = this.rnd();
      const kit = this.sinks.roofKit;
      // (each kind's roof-kit model first, the procedural boxes when the street pack did not load)
      if (kind < 0.3 && kit?.waterTank) this.kitModel(kit.waterTank, this.mid, lx, y, lz, 2.4, 2.2, 1.8);
      else if (kind < 0.3) {
        // water tank on legs
        this.boxL(lx, y, lz, 2.2, 0.8, 1.6, C.metal, T.white, undefined, this.mid, AO.roof);
        this.boxL(lx, y + 0.8, lz, 2.4, 1.4, 1.8, [0.32, 0.3, 0.28], T.corrugated, undefined, this.mid);
      } else if (kind < 0.55 && kit?.skylight) this.kitModel(kit.skylight, this.mid, lx, y, lz, 2.6, 0.75, 1.8);
      else if (kind < 0.55) {
        // skylight: a glazed hip on a kerb, the glass a shade lighter where it faces the sky
        this.boxL(lx, y, lz, 2.6, 0.3, 1.8, C.lead, T.white, undefined, this.mid, AO.roof);
        const p = this.f.p(lx, y, lz);
        this.mid.box(p[0], y + 0.3, p[2], 2.4, 0.45, 1.6, this.lot.angle, [0.9, 0.95, 1.0], tileUV(T.glazingDark), { uvTop: tileUV(T.glazingDark), topColour: [1.0, 1.05, 1.1] });
      } else if (kind < 0.65 && kit?.vent) {
        // a ventilator cowl on its flashing plate
        this.kitModel(kit.vent, this.mid, lx, y, lz, 1.3, 1.4, 1.3);
      } else if (kind < 0.75 && kit?.ventStack) this.kitModel(kit.ventStack, this.mid, lx, y, lz, 0.72, 2.1, 0.72);
      else if (kind < 0.75) {
        // vent stack with a cowl
        const p = this.f.p(lx, y, lz);
        this.mid.cylinder(p[0], p[2], y, y + 1.8, 0.25, 0.22, 8, C.metal, WHITE, { baseShade: AO.roof });
        this.fine.cylinder(p[0], p[2], y + 1.8, y + 2.1, 0.32, 0.12, 8, C.dark, WHITE);
      } else if (kind < 0.88 && kit?.house) this.kitModel(kit.house, this.mid, lx, y, lz, 3, 2.5, 3, 0, this.tint());
      else if (kind < 0.88) {
        // lift / stair housing with its own little felt roof and a door
        this.boxL(lx, y, lz, 3, 2.4, 3, this.tint(), this.skinPlain(), T.felt, this.mid, AO.roof);
        this.boxL(lx, y + 2.4, lz, 3.2, 0.1, 3.2, C.coping, T.white, undefined, this.mid);
      } else if (kit?.aerial) this.aerialModel(kit.aerial, lx, y, lz);
      else {
        // a TV aerial on a pole, guyed
        const p = this.f.p(lx, y, lz);
        this.fine.cylinder(p[0], p[2], y, y + 3.2, 0.03, 0.03, 4, C.metal, WHITE, { top: false });
        this.boxL(lx, y + 2.9, lz, 1.6, 0.04, 0.04, C.metal, T.white, undefined, this.fine);
        this.boxL(lx, y + 2.5, lz, 1.1, 0.04, 0.04, C.metal, T.white, undefined, this.fine);
      }
    }
  }

  /**
   * Chimney stack on a pitched roof at local (cx, cz): brick shaft, lead flashing where it meets the
   * slates (HIGH), cement flaunching, a row of pots in buff / red / black of uneven heights (HIGH), and
   * the soot it has thrown down the slope on the leeward side.
   */
  chimneyStack(cx: number, cz: number, yb: number, w: number, d: number, h: number, pots: number, salt: number): void {
    const kit = this.backdrop ? null : this.sinks.roofKit;
    // the narrow two-pot stack for the short rows, the long row of pots for the rest
    const model = kit ? (pots <= 2 ? (kit.chimneyB ?? kit.chimneyA) : (kit.chimneyA ?? kit.chimneyB)) : null;
    const shaft = model ? this.sinks.cb.get('facadeLow', this.lot.center.x, this.lot.center.z) : this.b;
    this.boxL(cx, yb, cz, w, h, d, C.chimney, T.brickPlain, T.brickSoot, shaft, AO.chimney);
    this.boxL(cx, yb + h, cz, w + 0.15, 0.12, d + 0.15, [0.3, 0.28, 0.26], T.white, undefined, shaft);
    if (model) {
      // shaft, cap and pots in one: the procedural stack's box plus a pot's height on top
      // toned down: the pale cement cap under the moon read as a white box on every roof at night
      this.kitModel(model, this.sinks.cb.get('facadeModel', this.lot.center.x, this.lot.center.z), cx, yb, cz, w + 0.15, h + 0.7, d + 0.15, Math.PI / 2, [0.8, 0.78, 0.76]);
      return;
    }
    if (this.backdrop) return;
    this.boxL(cx, yb + 0.12, cz, w + 0.2, 0.24, d + 0.2, [0.2, 0.21, 0.22], T.white, undefined, this.fine);
    const potC: RGB[] = [C.pot, [0.62, 0.5, 0.36], [0.5, 0.26, 0.18], [0.12, 0.11, 0.1]];
    for (let k = 0; k < pots; k++) {
      const pz = cz - d / 2 + 0.25 + (k + 0.5) * ((d - 0.5) / pots);
      const p = this.f.p(cx, 0, pz);
      const ph = 0.4 + hash01(this.id, salt * 7 + k, 6) * 0.3;
      const pc = potC[Math.floor(hash01(this.id, salt * 7 + k, 8) * potC.length)]!;
      // the pots read from the gameplay camera (a row of dots on every stack), so they ride on MEDIUM too
      (CLASSIC_BUILDINGS ? this.fine : this.mid).cylinder(p[0], p[2], yb + h + 0.12, yb + h + 0.12 + ph, 0.13, 0.11, 6, pc, WHITE, { topColour: [0.03, 0.03, 0.03] });
    }
  }

  /**
   * A roof-kit model in the box (sx along local x, sy, sz along local z) standing at (lx, y, lz), turned by `yaw`
   * in the lot frame (a quarter turn puts the model's x along the lot's z), into builder `b`.
   */
  kitModel(model: PackModel, b: MeshBuilder, lx: number, y: number, lz: number, sx: number, sy: number, sz: number, yaw = 0, tint?: RGB): void {
    const bb = model.geometry.boundingBox!;
    const quarter = Math.abs(Math.sin(yaw)) > 0.5;
    const mx = Math.max(1e-3, bb.max.x - bb.min.x);
    const my = Math.max(1e-3, bb.max.y);
    const mz = Math.max(1e-3, bb.max.z - bb.min.z);
    _ks.set((quarter ? sz : sx) / mx, sy / my, (quarter ? sx : sz) / mz);
    const p = this.f.p(lx, y, lz);
    _kq.setFromAxisAngle(_ky, this.lot.angle + yaw);
    _km.compose(_kp.set(p[0], p[1], p[2]), _kq, _ks);
    emitStreetModel(b, model, _km, { foot: AO.roof, footHeight: 0.4, uv: WHITE_UV, ...(tint ? { tint, untinted: tint } : {}) });
  }

  /**
   * The roof-kit Yagi with its mast foot at (lx, y, lz), `scale` × its 3.2 m, the boom turned to the borough's
   * transmitter whatever way the lot faces (HIGH only, as the procedural aerials).
   */
  aerialModel(model: PackModel, lx: number, y: number, lz: number, scale = 1): void {
    const p = this.f.p(lx, y, lz);
    _kq.setFromAxisAngle(_ky, AERIAL_HEADING);
    _ks.set(scale, scale, scale);
    _km.compose(_kp.set(p[0], p[1], p[2]), _kq, _ks);
    emitStreetModel(this.fine, model, _km, { uv: WHITE_UV });
  }

  skinPlain(): number {
    return this.skin().plain;
  }

  skin(): Skin {
    const s = this.lot.style;
    const r = hash01(this.id, 21);
    switch (s) {
      case 'warehouse':
        return SKIN.warehouse;
      case 'factory':
        return SKIN.factory;
      case 'shed':
        return SKIN.corrugated;
      case 'flats':
        return r < 0.6 ? SKIN.concrete : SKIN.brick;
      case 'office':
        return r < 0.5 ? SKIN.stone : SKIN.stucco;
      case 'bank':
      case 'church':
        return SKIN.stone;
      case 'club':
        return SKIN.stucco;
      case 'shopfront':
      case 'cafe':
        return r < 0.25 ? SKIN.stucco : r < 0.55 ? SKIN.stock : SKIN.brick;
      case 'garage':
      case 'lockup':
        return SKIN.soot;
      default:
        return r < 0.15 ? SKIN.soot : r < 0.45 ? SKIN.stock : SKIN.brick;
    }
  }

  /** Share of upper windows boarded up (empty flats over shops, run-down terraces, dead warehouses). */
  boardedChance(): number {
    if (this.has('safehouse') || this.lot.name) return 0;
    const poor = hash01(this.id, 61);
    if (poor > 0.22) return 0;
    switch (this.lot.style) {
      case 'terrace':
      case 'shopfront':
        return 0.12;
      case 'warehouse':
      case 'factory':
        return 0.2;
      default:
        return 0.05;
    }
  }

  litChance(): number {
    switch (this.lot.style) {
      case 'terrace':
        return 0.4;
      case 'flats':
        return 0.45;
      case 'pub':
      case 'club':
      case 'cafe':
        return 0.7;
      case 'shopfront':
        return 0.3;
      case 'office':
      case 'bank':
        return 0.2;
      case 'station':
        return 0.6;
      case 'warehouse':
        return 0.08;
      case 'factory':
        return 0.3;
      default:
        return 0.2;
    }
  }

  winTile(sk: Skin, col: number, row: number, face: number): number {
    if ((sk === SKIN.brick || sk === SKIN.soot) && row > 0 && hash01(this.id * 13 + face, col, row + 7) < this.boardedChance()) return T.brickWinBoarded;
    const lit = hash01(this.id * 7 + face, col, row) < this.litChance();
    const list = lit ? sk.lit : sk.win;
    return list[Math.floor(hash01(this.id, col * 3 + face, row + 11) * list.length)]!;
  }

  /**
   * Four walls with window grids; front gets `frontTile` override for the ground floor. Window cells
   * on the front and the open sides get a projecting sill (HIGH), and the floor lines a string course
   * (HIGH and MEDIUM) unless `courses` is off.
   */
  walls(rows: number[], bay: number, sk: Skin, ground?: (col: number, cols: number) => number, opts: { backWindows?: number; sideWindows?: boolean; sills?: boolean; courses?: boolean; skipGround?: (face: number) => boolean } = {}): void {
    const W = this.W;
    const D = this.D;
    const tint = this.tint();
    const faces: { a: [number, number]; b: [number, number]; open: boolean; len: number; face: number }[] = [
      { a: [-W / 2, D / 2], b: [W / 2, D / 2], open: true, len: W, face: 0 },
      { a: [W / 2, D / 2], b: [W / 2, -D / 2], open: this.has('open-left'), len: D, face: 1 },
      { a: [W / 2, -D / 2], b: [-W / 2, -D / 2], open: this.has('open-back'), len: W, face: 2 },
      { a: [-W / 2, -D / 2], b: [-W / 2, D / 2], open: this.has('open-right'), len: D, face: 3 },
    ];
    const backWin = opts.backWindows ?? 0.7;
    const sills = opts.sills ?? true;
    for (const fc of faces) {
      const cols = Math.max(1, Math.round(fc.len / bay));
      const isFront = fc.face === 0;
      const windowed = isFront || fc.open || (fc.face === 2 && backWin > 0) || (opts.sideWindows ?? false);
      this.wallGrid(
        fc.a,
        fc.b,
        rows,
        cols,
        (col, row) => {
          if (isFront && row === 0 && ground) return ground(col, cols);
          if (row === 0 && opts.skipGround?.(fc.face)) return -1;
          if (!windowed) return sk.plain;
          if (!isFront && !fc.open && fc.face === 2 && hash01(this.id, col, row + 50) > backWin) return sk.plain;
          if ((fc.face === 1 || fc.face === 3) && !fc.open && hash01(this.id, col, row + 80) > 0.35) return sk.plain;
          if (row === 0 && fc.open && !isFront && col === Math.floor(cols / 2) && cols > 2) return sk.door;
          return this.winTile(sk, col, row, fc.face);
        },
        tint,
        sills && (isFront || fc.open) ? sk : undefined,
      );
    }
    if (opts.courses ?? true) this.stringCourses(rows, sk);
  }

  // ------------------------------------------------------------------ styles

  build(): void {
    switch (this.lot.style) {
      case 'terrace':
        return this.terrace();
      case 'shopfront':
      case 'cafe':
        return this.shopfront();
      case 'pub':
        return this.pub();
      case 'club':
        return this.club();
      case 'warehouse':
        return this.warehouse();
      case 'factory':
        return this.factory();
      case 'shed':
        return this.shed();
      case 'lockup':
        return this.lockup();
      case 'garage':
        return this.garage();
      case 'flats':
        return this.flats();
      case 'office':
        return this.office();
      case 'bank':
        return this.bank();
      case 'church':
        return this.has('tower') ? this.churchTower() : this.churchNave();
      case 'station':
        return this.station();
      case 'chapel':
        return this.churchNave();
      default:
        return this.office();
    }
  }

  floors(height: number, floorH: number, groundH = floorH): number[] {
    const n = Math.max(1, Math.round((height - groundH) / floorH) + 1);
    const rows = [groundH];
    const rest = (height - groundH) / Math.max(1, n - 1);
    for (let i = 1; i < n; i++) rows.push(rest);
    return n === 1 ? [height] : rows;
  }

  terrace(): void {
    const units = Math.max(1, Number(this.tagValue('units:') ?? Math.round(this.W / 5.2)));
    const unitW = this.W / units;
    const skr = hash01(this.id, 31);
    const sk = skr < 0.15 ? SKIN.stucco : skr < 0.5 ? SKIN.stock : SKIN.brick;
    const stock = sk === SKIN.stock;
    const rows = [2.9, this.H - 2.9];
    const tint = this.tint();
    const W = this.W;
    const D = this.D;
    // Front: each house = door bay + window bay; upper floor 2 windows.
    const cols = units * 2;
    const flipped = (u: number): boolean => hash01(this.id, u, 7) < 0.5;
    const safe = this.has('safehouse');
    this.wallGrid(
      [-W / 2, D / 2],
      [W / 2, D / 2],
      rows,
      cols,
      (col, row) => {
        const u = Math.floor(col / 2);
        const doorCol = flipped(u) ? 1 : 0;
        if (row === 0 && col % 2 === doorCol) return sk.door;
        if (row === 0) return sk.plain; // bay window box covers this cell
        if (safe && u === 0) return T.brickWinLit;
        return this.winTile(sk, col, row, 0);
      },
      tint,
      sk,
    );
    // a stone band at first-floor sill level on the stock-brick and stucco terraces
    if (!this.backdrop && (stock || sk === SKIN.stucco)) this.ledge([-W / 2, D / 2], [W / 2, D / 2], 2.9 + 0.76, 0.12, 0.08, stock ? C.stone : [0.8, 0.78, 0.72], this.mid);
    // Back, sides
    this.wallGrid([W / 2, -D / 2], [-W / 2, -D / 2], rows, cols, (col, row) => (row === 0 && col % 2 === 0 ? sk.plain : this.winTile(sk, col, row, 2)), tint);
    for (const [a, b, open] of [
      [[W / 2, D / 2], [W / 2, -D / 2], this.has('open-left')],
      [[-W / 2, -D / 2], [-W / 2, D / 2], this.has('open-right')],
    ] as const) {
      const n = Math.max(1, Math.round(D / 3));
      this.wallGrid(a as [number, number], b as [number, number], rows, n, () => (open ? T.brickGable : sk.plain), tint);
    }
    // Bay windows, door colour, steps
    for (let u = 0; u < units; u++) {
      const x0 = -W / 2 + u * unitW;
      const doorCol = flipped(u) ? 1 : 0;
      const bayCentre = x0 + (doorCol === 0 ? unitW * 0.75 : unitW * 0.25);
      const doorCentre = x0 + (doorCol === 0 ? unitW * 0.25 : unitW * 0.75);
      const bw = unitW * 0.44;
      const bd = 0.75;
      // canted bay: front + two angled cheeks
      const z0 = D / 2;
      const zf = D / 2 + bd;
      const lit = hash01(this.id, u, 99) < this.litChance() || (safe && u === 0);
      const nets = hash01(this.id, u, 98) < 0.55;
      const front = stock ? (lit ? T.stockWinLit : nets ? T.stockWinNets : T.stockWinDark) : lit ? T.brickWinLit : nets ? T.brickWinNets : T.brickWinDark;
      const cheek = stock ? (lit ? T.stockWinLit2 : T.stockWinDark) : lit ? T.brickWinLit2 : T.brickWinDark2;
      this.quadL([bayCentre - bw / 2 + 0.25, zf], [bayCentre + bw / 2 - 0.25, zf], 0.2, 2.75, front, tint);
      this.quadL([bayCentre - bw / 2, z0], [bayCentre - bw / 2 + 0.25, zf], 0.2, 2.75, cheek, tint);
      this.quadL([bayCentre + bw / 2 - 0.25, zf], [bayCentre + bw / 2, z0], 0.2, 2.75, cheek, tint);
      this.boxL(bayCentre, 0, (z0 + zf) / 2, bw, 0.2, bd, C.stone);
      // bay roof: lead on the top, a stone cornice line under it, a sill below the glass
      this.boxL(bayCentre, 2.75, (z0 + zf) / 2 + 0.05, bw + 0.1, 0.22, bd + 0.15, C.lead);
      if (!this.backdrop) {
        this.boxL(bayCentre, 2.62, (z0 + zf) / 2 + 0.08, bw + 0.18, 0.13, bd + 0.2, C.cream, T.white, undefined, this.mid);
        this.boxL(bayCentre, 0.2, zf + 0.02, bw - 0.4, 0.08, 0.12, C.stone, T.white, undefined, this.fine);
      }
      // door paint
      const dc = safe && u === 0 ? C.bottle : DOOR_COLOURS[Math.floor(hash01(this.id, u, 4) * DOOR_COLOURS.length)]!;
      this.quadL([doorCentre - 0.4, D / 2], [doorCentre + 0.4, D / 2], 0.12, 2.0, T.white, mulRGB(dc, AO.recess), 0.03);
      this.boxL(doorCentre, 0, D / 2 + 0.25, 1.2, 0.14, 0.5, C.stone);
      // painted stucco ground-floor band on some houses (the "done-up" ones), whitened step
      if (!stock && sk !== SKIN.stucco && hash01(this.id, u, 41) < 0.18) {
        const tone = 0.8 + hash01(this.id, u, 42) * 0.15;
        this.quadL([x0 + 0.05, D / 2 + 0.015], [doorCentre - 0.55, D / 2 + 0.015], 0, 0.9, T.white, [tone, tone * 0.97, tone * 0.9]);
        this.quadL([doorCentre + 0.55, D / 2 + 0.015], [x0 + unitW - 0.05, D / 2 + 0.015], 0, 0.9, T.white, [tone, tone * 0.97, tone * 0.9]);
      }
      // rear outrigger (scullery) for every other house
      if (u % 2 === 0 && D > 7) {
        const ow = Math.min(2.6, unitW * 0.5);
        const ox = x0 + unitW * (doorCol === 0 ? 0.3 : 0.7);
        const od = 3.2;
        this.boxL(ox, 0, -D / 2 - od / 2, ow, 2.6, od, tint, sk.plain, T.slate);
      }
    }
    if (this.has('garden')) {
      // nothing more; garden walls are part of the decor walls
    }
    // Cast-iron gutter along the front eaves (HIGH and MEDIUM) and a downpipe on every party wall (HIGH).
    if (!this.backdrop) {
      this.boxL(0, this.H - 0.22, D / 2 + 0.32, W + 0.2, 0.14, 0.16, C.dark, T.white, undefined, this.mid);
      for (let u = 0; u <= units; u++) {
        const x = Math.max(-W / 2 + 0.12, Math.min(W / 2 - 0.12, -W / 2 + u * unitW));
        this.boxL(x, 0.1, D / 2 + 0.08, 0.1, this.H - 0.3, 0.1, C.dark, T.white, undefined, this.fine);
      }
    }
    // Roof with chimney stacks on the party walls.
    const pitch = 0.62;
    const rise = this.gableRoof(this.H, pitch, 'x');
    for (let u = 0; u < units; u++) {
      const cx = -W / 2 + (u + 0.5) * unitW;
      const dr = hash01(this.id, u, 57);
      // Loft rooms: a dormer in the front slope on some houses, a rooflight in the rear slope on others.
      if (!this.backdrop && dr < 0.14) this.dormer(cx, pitch, sk, 1, hash01(this.id, u, 58) < 0.5);
      else if (dr < 0.38) this.slopePatch(cx - 0.45, cx + 0.45, 0.3, 0.55, -1, pitch, T.glazingDark, [0.9, 0.95, 1.0], this.fine);
      // Soot from the stacks has blackened the slates down the leeward side of each party wall.
      if (!this.backdrop && hash01(this.id, u, 59) < 0.5) {
        const sx = -W / 2 + u * unitW;
        const side: 1 | -1 = hash01(this.id, u, 60) < 0.5 ? 1 : -1;
        this.slopePatch(sx + 0.2, sx + 1.2, 0.55, 0.96, side, pitch, T.slate, [0.5, 0.5, 0.53], this.fine);
      }
    }
    const kitAerial = this.backdrop ? null : (this.sinks.roofKit?.aerial ?? null);
    for (let u = 0; u <= units; u++) {
      const x = -W / 2 + u * unitW;
      const cx = Math.max(-W / 2 + 0.4, Math.min(W / 2 - 0.4, x));
      const yb = this.H + rise - 0.6;
      this.chimneyStack(cx, 0, yb, 0.75, 1.5, 1.9, 2 + Math.floor(hash01(this.id, u, 5) * 3), u);
      // lead apron round the foot of the stack on both slopes (HIGH only): the grey collar every stack has
      // from the air, 30 cm out from the brick
      if (!this.backdrop) for (const side of [1, -1] as const) this.slopePatch(cx - 0.62, cx + 0.62, 1 - 1.05 / (D / 2), 1, side, pitch, T.lead, C.lead, this.fine);
      // TV aerial on some stacks: the roof-kit Yagi bracketed to the stack (its mast foot 0.3 m down the shaft,
      // 1.9 m of it above the pots), else the pole with two crossbars
      if (!this.backdrop && hash01(this.id, u, 77) < 0.45 && kitAerial) this.aerialModel(kitAerial, cx, yb + 1.9 - 0.3, 0.3, 0.68);
      else if (!this.backdrop && hash01(this.id, u, 77) < 0.45) {
        const p = this.f.p(cx, 0, 0.3);
        this.fine.cylinder(p[0], p[2], yb + 1.9, yb + 3.6, 0.03, 0.03, 4, C.metal, WHITE, { top: false });
        this.boxL(cx, yb + 3.3, 0.3, 1.6, 0.05, 0.05, C.metal, T.white, undefined, this.fine);
        this.boxL(cx, yb + 3.0, 0.3, 1.1, 0.05, 0.05, C.metal, T.white, undefined, this.fine);
      }
    }
    // Gable-end billboard.
    const bill = this.tagValue('billboard:');
    if (bill) {
      const openLeft = this.has('open-left');
      const lx = openLeft ? W / 2 + 0.05 : -W / 2 - 0.05;
      this.signL(bill, 'billboard', lx, 3.1, 0, Math.min(D - 1, 7.5), false, openLeft ? Math.PI / 2 : -Math.PI / 2);
    }
    if (safe) this.glowBox(-W / 2 + unitW * 0.5, 2.3, D / 2 + 0.25, 0.25, 0.3, 0.2, [3, 2.1, 1.1]);
  }

  shopfront(): void {
    const sk = this.skin();
    const cafe = this.lot.style === 'cafe';
    const groundH = 3.6;
    const rows = this.floors(this.H, 3.0, groundH);
    const bay = 3.6;
    const kind = shopKind(this.lot.name, cafe);
    const shut = kind === 'plain' && hash01(this.id, 8) < 0.12;
    this.fasciaY = groundH;
    // the flats over and the sides as before; the front ground row is the shop units below
    this.walls(rows, bay, sk, () => -1);
    const W = this.W;
    const D = this.D;
    const z = D / 2;
    const tint = this.tint();
    // SHOP-FRONT modules (docs/research/BLENDER-BUILDINGS.md §3, P2): one shop per 4.6–6.5 m unit, each a
    // window cell (the baked 3.6 m tile stretched to the unit less the door) and a 1.8 m door cell on the
    // side the hash says (the door tile is mirrored for a door on the right); a shut shop has its shutter
    // down over the whole unit. Pilasters stand on the unit lines.
    const units = Math.max(1, Math.round(W / 5.4));
    const uw = W / units;
    const under = this.has('awning') ? AO.awning : AO.fascia;
    const doorUV = tileUV(T.shopDoorLit);
    for (let u = 0; u < units; u++) {
      const x0 = -W / 2 + u * uw;
      const right = hash01(this.id, u, 23) < 0.5;
      const r = hash01(this.id, u, 2);
      // the draper's window and the shut shop only where the name says nothing about the trade
      const win = shut ? T.shopShutter : kind === 'launderette' ? T.launderette : kind === 'bookie' ? T.bookieWin : kind === 'cafe' ? (r < 0.6 ? T.cafeWin : T.shopWinLit2) : kind === 'plain' && r < 0.35 ? T.shopWinLit2 : kind === 'plain' && r < 0.5 ? T.shopWinDark : T.shopWinLit;
      const dw = shut ? 0 : 1.8;
      const wx0 = right ? x0 : x0 + dw;
      if (shut && u % 2) {
        // every other shutter mirrored, so the bill pasted on it does not repeat along the row
        const s = tileUV(T.shopShutter);
        this.b.quad(this.f.p(wx0, 0, z), this.f.p(wx0 + uw, 0, z), this.f.p(wx0 + uw, groundH, z), this.f.p(wx0, groundH, z), [s[2], s[1], s[0], s[3]], mulRGB(tint, footShade(0)), mulRGB(tint, under));
      } else this.quadL([wx0, z], [wx0 + uw - dw, z], 0, groundH, win, tint, 0, this.b, under);
      if (dw > 0) {
        const dx0 = right ? x0 + uw - dw : x0;
        const uv: [number, number, number, number] = right ? [doorUV[2], doorUV[1], doorUV[0], doorUV[3]] : doorUV;
        this.b.quad(this.f.p(dx0, 0, z), this.f.p(dx0 + dw, 0, z), this.f.p(dx0 + dw, groundH, z), this.f.p(dx0, groundH, z), uv, mulRGB(tint, footShade(0)), mulRGB(tint, under));
      }
    }
    // fascia board + name, under a moulded cornice carried on console brackets over the pilasters
    const boardC = [C.bottle, C.oxblood, C.navy, C.dark][Math.floor(hash01(this.id, 12) * 4)]!;
    // painted stone, a few years of soot on it: full cream still burned out to white under the sky
    const pilC: RGB = hash01(this.id, 13) < 0.5 ? [0.72, 0.68, 0.58] : [boardC[0] * 0.8 + 0.1, boardC[1] * 0.8 + 0.1, boardC[2] * 0.8 + 0.1];
    this.boxL(0, groundH - 0.05, D / 2 + 0.15, W - 0.2, 0.75, 0.3, boardC);
    this.boxL(0, groundH + 0.7, D / 2 + 0.2, W, 0.12, 0.42, pilC);
    // the pilaster shafts at the lot ends are the silhouette (every tier); the pilasters on the inner unit
    // lines, every plinth and console are MEDIUM / HIGH; neckings and the bed mould under the cornice HIGH
    for (const x of [-W / 2 + 0.15, W / 2 - 0.15]) this.boxL(x, 0, D / 2 + 0.1, 0.35, groundH + 0.6, 0.25, pilC);
    if (!this.backdrop) {
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], groundH + 0.82, 0.14, 0.5, pilC, this.mid);
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], groundH + 0.6, 0.06, 0.36, mulRGB(pilC, 0.82), this.fine);
      for (let u = 0; u <= units; u++) {
        const x = u === 0 ? -W / 2 + 0.15 : u === units ? W / 2 - 0.15 : -W / 2 + u * uw;
        if (u > 0 && u < units) this.boxL(x, 0, D / 2 + 0.1, 0.35, groundH + 0.6, 0.25, pilC, T.white, undefined, this.mid);
        this.boxL(x, 0, D / 2 + 0.14, 0.42, 0.5, 0.3, mulRGB(pilC, 0.6), T.white, undefined, this.mid);
        this.boxL(x, groundH - 0.4, D / 2 + 0.13, 0.41, 0.07, 0.31, mulRGB(pilC, 0.9), T.white, undefined, this.fine);
        this.boxL(x, groundH + 0.2, D / 2 + 0.34, 0.32, 0.5, 0.26, pilC, T.white, undefined, this.mid);
      }
    }
    const lit = cafe || this.lot.tags.includes('lit') || kind === 'launderette' || kind === 'bookie' || (kind !== 'plain' && hash01(this.id, 14) < 0.5);
    if (this.lot.name) this.signL(this.lot.name, 'fascia', 0, groundH + 0.0, D / 2 + 0.31, Math.min(W - 0.6, 7.5), lit);
    // a projecting sign on an iron bracket, so the name reads up and down the street
    if (!this.backdrop && this.lot.name && kind !== 'bookie' && hash01(this.id, 15) < 0.4) {
      const sx = hash01(this.id, 16) < 0.5 ? W / 2 - 0.55 : -W / 2 + 0.55;
      this.boxL(sx, groundH + 1.15, D / 2 + 0.55, 0.06, 0.06, 1.1, C.dark, T.white, undefined, this.fine);
      this.signL(this.lot.name, 'fascia', sx, groundH + 0.55, D / 2 + 0.75, 1.3, lit, Math.PI / 2);
      this.signL(this.lot.name, 'fascia', sx, groundH + 0.55, D / 2 + 0.75, 1.3, lit, -Math.PI / 2);
    }
    if (this.has('awning') && !shut) this.awning(groundH - 0.15, units, uw);
    this.shopRoof(sk);
    // shop light spill strip under the fascia (launderettes burn cold fluorescent tubes)
    // (kept under the tone-mapper's knee: at 2.4 the tube burned out to a white bar down the street)
    this.glowBox(0, groundH - 0.25, D / 2 + 0.32, W - 0.6, 0.06, 0.04, kind === 'launderette' ? [0.75, 1.0, 1.0] : [1.0, 0.76, 0.46]);
    this.shopDressing(kind, groundH);
  }

  /** Trade-specific street clutter in front of a shop. */
  shopDressing(kind: ShopKind, groundH: number): void {
    const W = this.W;
    const D = this.D;
    const z = D / 2;
    switch (kind) {
      case 'pawn': {
        // three gilt balls on a wrought bracket
        const bx = W / 2 - 0.6;
        this.boxL(bx, groundH + 1.0, z + 0.55, 0.06, 0.06, 1.1, C.dark);
        for (const [dx, dy] of [
          [-0.22, 0],
          [0.22, 0],
          [0, -0.36],
        ] as const) {
          const p = this.f.p(bx + dx, 0, z + 1.05);
          this.b.cylinder(p[0], p[2], groundH + 0.62 + dy, groundH + 0.92 + dy, 0.17, 0.17, 8, C.gold, WHITE);
        }
        break;
      }
      case 'produce': {
        // trestle display of boxed goods under the awning
        const n = Math.max(2, Math.floor((W - 2) / 1.1));
        const cols: RGB[] = [
          [0.55, 0.16, 0.08],
          [0.2, 0.38, 0.12],
          [0.72, 0.52, 0.12],
          [0.62, 0.3, 0.1],
          [0.5, 0.48, 0.42],
        ];
        this.boxL(0, 0.55, z + 0.55, Math.min(W - 1.5, n * 1.0), 0.06, 0.8, [0.35, 0.26, 0.18], T.timber);
        for (let i = 0; i < n; i++) {
          const x = -((n - 1) * 1.0) / 2 + i * 1.0;
          if (Math.abs(x) > W / 2 - 0.8) continue;
          this.boxL(x, 0.61, z + 0.55, 0.85, 0.18, 0.65, cols[Math.floor(hash01(this.id, i, 71) * cols.length)]!);
        }
        for (const sx of [-1, 1]) this.boxL((sx * Math.min(W - 1.6, n * 1.0)) / 2, 0, z + 0.55, 0.06, 0.55, 0.7, C.dark);
        break;
      }
      case 'news': {
        // A-board with the evening headline, and a bundle of papers by the door
        const bill = NEWS_BILLS[Math.floor(hash01(this.id, 81) * NEWS_BILLS.length)]!;
        const bx = -W / 2 + 1.2;
        this.signL(bill, 'aboard', bx, 0.1, z + 0.86, 0.6, false);
        this.boxL(bx, 0, z + 0.82, 0.62, 0.9, 0.06, C.dark);
        break;
      }
      case 'hardware': {
        // buckets, brooms and a paraffin can set out on the pavement
        for (let i = 0; i < 4; i++) {
          const p = this.f.p(-W / 2 + 1 + i * 0.5, 0, z + 0.35);
          const tin: RGB = i % 2 ? [0.5, 0.52, 0.52] : [0.62, 0.18, 0.12];
          this.b.cylinder(p[0], p[2], 0, 0.36, 0.17, 0.2, 6, tin, WHITE, { baseShade: AO.foot });
        }
        for (let i = 0; i < 3; i++) this.boxL(W / 2 - 1.0 - i * 0.18, 0, z + 0.08, 0.05, 1.3, 0.05, [0.5, 0.38, 0.2]);
        break;
      }
      case 'cafe': {
        // a menu board on the pavement
        this.signL('MENU · TEA & TOAST ALL DAY', 'aboard', W / 2 - 1.2, 0.1, z + 0.74, 0.56, false);
        this.boxL(W / 2 - 1.2, 0, z + 0.7, 0.58, 0.8, 0.05, C.dark);
        break;
      }
      case 'bookie': {
        // blade sign so punters find it from up the street
        this.boxL(W / 2 - 0.4, groundH + 0.9, z + 0.75, 0.06, 0.06, 1.5, C.dark);
        this.signL('TURF ACCOUNTANT', 'fascia', W / 2 - 0.4, groundH + 0.3, z + 0.75, 1.5, true, Math.PI / 2);
        this.signL('TURF ACCOUNTANT', 'fascia', W / 2 - 0.4, groundH + 0.3, z + 0.75, 1.5, true, -Math.PI / 2);
        break;
      }
      default:
        break;
    }
  }

  shopRoof(sk: Skin): void {
    if (hash01(this.id, 41) < 0.5 || this.D > 16) {
      this.flatRoof(this.H, 0.8, T.felt, C.coping, true, true);
      // brick stack on the party wall with a row of pots
      const sx = (hash01(this.id, 46) < 0.5 ? -1 : 1) * (this.W / 2 - 0.45);
      this.chimneyStack(sx, -this.D / 4, this.H, 0.7, 1.6, 2.0, 3, 11);
    } else {
      // parapet front + pitched roof behind, a stone cornice under the parapet
      this.boxL(0, this.H, this.D / 2 - 0.2, this.W, 1.0, 0.4, this.tint(), sk.plain, sk.plain);
      this.boxL(0, this.H + 1.0, this.D / 2 - 0.2, this.W + 0.1, 0.15, 0.55, C.coping);
      if (!this.backdrop) this.ledge([-this.W / 2, this.D / 2], [this.W / 2, this.D / 2], this.H - 0.25, 0.18, 0.3, C.cornice, this.mid);
      const pitch = 0.55;
      const rise = this.gableRoof(this.H, pitch, 'x');
      const cx = (hash01(this.id, 44) - 0.5) * (this.W - 2);
      this.chimneyStack(cx, 0, this.H + rise - 0.5, 0.8, 1.4, 1.8, 2, 12);
      if (!this.backdrop && hash01(this.id, 45) < 0.3) this.dormer(cx > 0 ? cx - 2.2 : cx + 2.2, pitch, sk, 1, hash01(this.id, 47) < 0.4);
    }
  }

  /**
   * Striped canvas awnings, one per shop unit (not over every unit), in the two baked colourways with a
   * little sun fade per awning; the tile is a 5 m run of 0.22 m stripes, so a unit maps to its share of it.
   */
  awning(y: number, units: number, uw: number): void {
    const W = this.W;
    const D = this.D;
    const out = 1.7;
    const drop = 0.75;
    for (let u = 0; u < units; u++) {
      if (units > 1 && hash01(this.id, u, 27) > 0.72) continue;
      const x0 = -W / 2 + u * uw + 0.3;
      const x1 = x0 + uw - 0.6;
      const uv = tileUV(hash01(this.id, u, 19) < 0.5 ? T.awning : T.awningBlue);
      const fade = 0.84 + hash01(this.id, u, 29) * 0.16;
      const c: RGB = [fade, fade, fade];
      const p0 = this.f.p(x0, y - drop, D / 2 + out);
      const p1 = this.f.p(x1, y - drop, D / 2 + out);
      const p2 = this.f.p(x1, y, D / 2 + 0.05);
      const p3 = this.f.p(x0, y, D / 2 + 0.05);
      this.b.quad(p0, p1, p2, p3, [uv[0], uv[1], uv[0] + (uv[2] - uv[0]) * Math.min(1, (x1 - x0) / 5), uv[3]], c);
      // valance
      const v0 = this.f.p(x0, y - drop - 0.3, D / 2 + out);
      const v1 = this.f.p(x1, y - drop - 0.3, D / 2 + out);
      this.b.quad(v0, v1, p1, p0, uv, mulRGB(c, 0.8));
      // the folding arms under the canvas (HIGH)
      if (!this.backdrop) for (const x of [x0 + 0.08, x1 - 0.08]) this.boxL(x, y - drop - 0.03, D / 2 + out / 2 + 0.05, 0.03, 0.04, out - 0.1, C.dark, T.white, undefined, this.fine);
    }
  }

  pub(): void {
    const groundH = 3.6;
    const rows = this.floors(this.H, 3.1, groundH);
    const W = this.W;
    const D = this.D;
    const tint = this.tint();
    const fasciaC: RGB = [0.06, 0.08, 0.07];
    this.fasciaY = groundH;
    // PUB-GROUND (docs/research/BLENDER-BUILDINGS.md P2): a corner pub on an open side has its ground-floor
    // corner chamfered to hold the door (the floors over it oversail on a stone corbel); the front and the
    // open side's ground rows are drawn here, with the etched-band windows and the door cells
    const side = this.has('corner') ? (this.has('open-left') ? 1 : this.has('open-right') ? -1 : 0) : 0;
    const ch = side ? 2.4 : 0;
    this.walls(rows, 3.4, SKIN.brick, () => -1, { sideWindows: true, skipGround: (face) => side !== 0 && face === (side > 0 ? 1 : 3) });
    const fx0 = side < 0 ? -W / 2 + ch : -W / 2;
    const fx1 = side > 0 ? W / 2 - ch : W / 2;
    const fcols = Math.max(1, Math.round((fx1 - fx0) / 3.4));
    const doorCol = side < 0 ? fcols - 1 : 0;
    this.wallGrid([fx0, D / 2], [fx1, D / 2], [groundH], fcols, (c) => (c === doorCol ? T.pubDoor : T.pubWinLit), tint);
    if (side) {
      const a: [number, number] = side > 0 ? [W / 2 - ch, D / 2] : [-W / 2, D / 2 - ch];
      const b: [number, number] = side > 0 ? [W / 2, D / 2 - ch] : [-W / 2 + ch, D / 2];
      this.quadL(a, b, 0, groundH, T.pubDoor, tint, 0, this.b, AO.fascia);
      this.quadL(a, b, groundH - 0.1, groundH + 0.7, T.white, fasciaC, 0.15);
      const sa: [number, number] = side > 0 ? [W / 2, D / 2 - ch] : [-W / 2, -D / 2];
      const sb: [number, number] = side > 0 ? [W / 2, -D / 2] : [-W / 2, D / 2 - ch];
      this.wallGrid(sa, sb, [groundH], Math.max(1, Math.round((D - ch) / 3.4)), () => T.pubWinLit, tint);
      // the corbel under the oversailing corner (its underside shows from the pavement, so it has a bottom)
      const p = this.f.p(side > 0 ? W / 2 - ch / 2 : -W / 2 + ch / 2, groundH - 0.45, D / 2 - ch / 2);
      this.b.box(p[0], p[1], p[2], ch + 0.02, 0.45, ch + 0.02, this.lot.angle, C.stone, WHITE, { bottom: true, baseShade: AO.recess });
    } else {
      // ground floor glazed tiles on the open side too
      if (this.has('open-left')) this.wallGrid([W / 2 + 0.02, D / 2], [W / 2 + 0.02, -D / 2], [groundH], Math.max(1, Math.round(D / 3.4)), (c) => (c === 0 ? T.pubDoor : T.pubWinLit), [1, 1, 1]);
      if (this.has('open-right')) this.wallGrid([-W / 2 - 0.02, -D / 2], [-W / 2 - 0.02, D / 2], [groundH], Math.max(1, Math.round(D / 3.4)), (c, n) => (c === n - 1 ? T.pubDoor : T.pubWinLit), [1, 1, 1]);
    }
    // fascia with gold lettering
    this.boxL(0, groundH - 0.1, D / 2 + 0.15, W, 0.8, 0.3, fasciaC);
    this.boxL(0, groundH + 0.7, D / 2 + 0.2, W + 0.1, 0.14, 0.44, C.gold);
    if (this.lot.name) this.signL(this.lot.name.toUpperCase(), 'pubBoard', 0, groundH - 0.05, D / 2 + 0.31, Math.min(W - 0.8, 9), true);
    // hanging sign on a bracket at the corner end of the front
    const sx = side < 0 ? fx0 + 0.6 : fx1 - 0.6;
    this.boxL(sx, groundH + 1.2, D / 2 + 0.75, 0.08, 0.08, 1.4, C.dark);
    this.signL(this.lot.name ?? 'PUB', 'hanging', sx, groundH + 0.05, D / 2 + 1.25, 0.95, true, Math.PI / 2);
    this.signL(this.lot.name ?? 'PUB', 'hanging', sx, groundH + 0.05, D / 2 + 1.25, 0.95, true, -Math.PI / 2);
    // carriage lamps on iron brackets: either side of the corner door, or of the front door
    const lamps: [number, number, number][] = side ? [[side > 0 ? fx1 - 0.6 : fx0 + 0.6, D / 2, 0], [side > 0 ? W / 2 : -W / 2, D / 2 - ch - 0.6, side]] : [[fx0 + 0.8, D / 2, 0], [fx0 + 3.0, D / 2, 0]];
    for (const [lx, lz, s] of lamps) this.lamp(lx, lz, s);
    // hanging flower baskets along the fascia
    for (let x = -W / 2 + 2.0; x < W / 2 - 1.5; x += 3.4) {
      this.boxL(x, groundH + 0.85, D / 2 + 0.45, 0.04, 0.04, 0.5, C.dark);
      // from the high camera only the top reads: a dark trailing-ivy ball crowned with a mass of
      // bedding plants, with a smaller tuft of a second colour so it reads as flowers, not a box
      const hb = hash01(this.id, Math.round(x * 10), 3);
      const bloom: RGB = hb < 0.35 ? [0.3, 0.03, 0.04] : hb < 0.7 ? [0.36, 0.25, 0.04] : [0.28, 0.07, 0.2];
      const tuft: RGB = hb < 0.5 ? [0.36, 0.34, 0.3] : [0.28, 0.05, 0.14];
      this.boxL(x, groundH + 0.22, D / 2 + 0.7, 0.62, 0.42, 0.62, [0.02, 0.045, 0.015]);
      this.boxL(x, groundH + 0.52, D / 2 + 0.7, 0.56, 0.14, 0.56, bloom);
      this.boxL(x + 0.12, groundH + 0.6, D / 2 + 0.62, 0.22, 0.1, 0.22, tuft);
      this.boxL(x - 0.14, groundH + 0.58, D / 2 + 0.82, 0.18, 0.1, 0.18, [0.03, 0.08, 0.025]);
    }
    this.eavesGutter(this.H);
    const rise = this.gableRoof(this.H, 0.6, 'x');
    for (const x of [-W / 2 + 0.6, W / 2 - 0.6]) this.chimneyStack(x, 0, this.H + rise - 0.8, 0.8, 1.6, 2.2, 3, x < 0 ? 13 : 14);
    if (!this.backdrop) this.dormer(hash01(this.id, 48) < 0.5 ? -W / 4 : W / 4, 0.6, SKIN.brick, 1, true);
  }

  /**
   * A carriage lamp on an iron bracket at (lx, lz): on the front wall (`side` 0, out along +Z) or on a side
   * wall (`side` ±1, out along ±X). The lantern glows in the `glow` builder; the bracket arm and the lantern's
   * cap are MEDIUM / HIGH detail.
   */
  lamp(lx: number, lz: number, side: number): void {
    const ox = side * 0.3;
    const oz = side ? 0 : 0.3;
    this.glowBox(lx + ox, 2.6, lz + oz, 0.28, 0.4, 0.28, [3.2, 2.2, 1.1]);
    if (this.backdrop) return;
    const b = this.mid;
    if (side) this.boxL(lx + side * 0.2, 3.02, lz, 0.4, 0.05, 0.05, C.dark, T.white, undefined, b);
    else this.boxL(lx, 3.02, lz + 0.2, 0.05, 0.05, 0.4, C.dark, T.white, undefined, b);
    this.boxL(lx + ox, 3.0, lz + oz, 0.34, 0.07, 0.34, C.dark, T.white, undefined, b);
    this.boxL(lx + ox, 2.52, lz + oz, 0.3, 0.08, 0.3, C.dark, T.white, undefined, b);
  }

  club(): void {
    const groundH = 3.8;
    const rows = this.floors(this.H, 3.2, groundH);
    const W = this.W;
    const D = this.D;
    this.walls(rows, 3.4, SKIN.stucco, (col) => (col === 1 ? T.shopDoorLit : T.clubFront));
    // canopy with glowing underside and a row of bulbs along its edge
    this.boxL(-W / 2 + 4.5, 3.0, D / 2 + 1.4, 4.2, 0.3, 2.8, [0.12, 0.04, 0.08]);
    this.glowBox(-W / 2 + 4.5, 2.95, D / 2 + 1.4, 3.8, 0.05, 2.4, [3.0, 1.2, 1.8]);
    for (let x = -W / 2 + 2.6; x <= -W / 2 + 6.4; x += 0.38) this.glowBox(x, 3.08, D / 2 + 2.78, 0.1, 0.1, 0.1, [3.2, 2.2, 1.0]);
    // vertical sign blade, outlined in pink neon tube
    this.boxL(W / 2 - 2.2, 4.4, D / 2 + 0.8, 0.25, 6.2, 1.5, [0.08, 0.05, 0.08]);
    this.signL(this.lot.name ?? 'CLUB', 'neon', W / 2 - 2.2, 4.5, D / 2 + 0.8, 1.6, true, Math.PI / 2);
    this.signL(this.lot.name ?? 'CLUB', 'neon', W / 2 - 2.2, 4.5, D / 2 + 0.8, 1.6, true, -Math.PI / 2);
    const neon: RGB = [3.4, 0.9, 1.8];
    for (const dz of [-0.78, 0.78]) this.glowBox(W / 2 - 2.2, 4.3, D / 2 + 0.8 + dz, 0.3, 6.4, 0.05, neon);
    for (const dy of [4.3, 10.65]) this.glowBox(W / 2 - 2.2, dy, D / 2 + 0.8, 0.3, 0.05, 1.6, neon);
    // a neon strip along the parapet, so the club reads from the end of the street
    this.glowBox(0, this.H + 0.85, D / 2 + 0.05, W - 1, 0.06, 0.06, [2.6, 0.7, 1.4]);
    this.flatRoof(this.H, 0.9, T.felt, C.coping, true, true);
  }

  warehouse(): void {
    const rows = this.floors(this.H, 3.5, 4.2);
    const W = this.W;
    const D = this.D;
    const hoist = this.has('hoist');
    const cols = Math.max(1, Math.round(W / 3.6));
    const mid = Math.floor(cols / 2);
    this.walls(rows, 3.6, SKIN.warehouse, (col) => (col === mid || hash01(this.id, col, 61) < 0.3 ? T.loadingDoor : T.rollerDoor), { backWindows: 0.5 });
    if (hoist) {
      // stacked loading doors in the middle bay + cathead hoist beam
      const bw = W / cols;
      const x = -W / 2 + bw * (mid + 0.5);
      let y = rows[0]!;
      for (let r = 1; r < rows.length; r++) {
        this.quadL([x - bw / 2, D / 2 + 0.01], [x + bw / 2, D / 2 + 0.01], y, y + rows[r]!, T.loadingDoor, this.tint());
        y += rows[r]!;
      }
      this.boxL(x, this.H - 0.6, D / 2 + 0.9, 0.35, 0.35, 1.8, C.dark);
      this.boxL(x, this.H - 0.9, D / 2 + 0.6, 0.6, 1.0, 1.2, [0.3, 0.22, 0.16], T.timber);
    }
    if (this.lot.name) this.signL(this.lot.name, 'painted', 0, this.H - 2.3, D / 2 + 0.03, Math.min(W - 2, 22), false);
    if (hash01(this.id, 70) < 0.5 || hoist) {
      const axis = hoist || D > W ? 'z' : 'x';
      const pitch = 0.48;
      this.gableRoof(this.H, pitch, axis, T.slate, T.brickSoot);
      if (!this.backdrop) {
        // long glazed rooflights on both slopes, and a gutter along the eaves
        const length = axis === 'x' ? W : D;
        const n = Math.max(1, Math.floor(length / 7));
        for (let i = 0; i < n; i++) {
          const u = -length / 2 + ((i + 0.5) * length) / n;
          for (const side of [1, -1] as const) {
            if (hash01(this.id, i * 2 + (side > 0 ? 0 : 1), 71) > 0.7) continue;
            this.slopePatch(u - 2.2, u + 2.2, 0.35, 0.6, side, pitch, T.glazingDark, [0.9, 0.95, 1.0], this.fine, axis);
          }
        }
        if (axis === 'x') this.eavesGutter(this.H, false);
      }
    } else {
      this.flatRoof(this.H, 1.0);
      // a brick band and a steel water tank on the bigger flat roofs
      if (!this.backdrop) this.ledge([-W / 2, D / 2], [W / 2, D / 2], this.H - 0.5, 0.2, 0.08, [0.3, 0.2, 0.16], this.mid);
    }
  }

  factory(): void {
    const W = this.W;
    const D = this.D;
    const rows = [Math.max(4, this.H - 1)];
    this.walls(rows, 4.2, SKIN.factory, (col) => (col % 3 === 1 ? T.rollerDoor : T.factoryWinLit), { backWindows: 0.6, sideWindows: true });
    const baseY = rows[0]!;
    // band course
    this.boxL(0, baseY, 0, W + 0.2, 0.4, D + 0.2, C.coping);
    if (this.has('sawtooth')) {
      const teeth = Math.max(2, Math.round(D / 5.5));
      const td = D / teeth;
      const rise = 2.6;
      const tint = this.roofTint();
      for (let i = 0; i < teeth; i++) {
        const z0 = -D / 2 + i * td;
        const z1 = z0 + td;
        const y0 = baseY + 0.4;
        // sloped roof from z0 (low) to z1 (high)
        const n = Math.max(1, Math.round(W / 4));
        for (let k = 0; k < n; k++) {
          const x0 = -W / 2 + (W * k) / n;
          const x1 = -W / 2 + (W * (k + 1)) / n;
          this.b.quadFacing(this.f.p(x1, y0, z0), this.f.p(x0, y0, z0), this.f.p(x0, y0 + rise, z1), this.f.p(x1, y0 + rise, z1), tileUV(T.corrugatedRoof), tint, UP);
          // glazed vertical face at z1, facing +Z
          this.b.quadFacing(this.f.p(x0, y0, z1), this.f.p(x1, y0, z1), this.f.p(x1, y0 + rise, z1), this.f.p(x0, y0 + rise, z1), tileUV(T.glazingLit), [1, 1, 1], this.f.d(0, 0, 1));
        }
        // end triangles
        for (const sx of [-1, 1]) {
          const a = this.f.p((sx * W) / 2, y0, z0);
          const bb = this.f.p((sx * W) / 2, y0, z1);
          const c = this.f.p((sx * W) / 2, y0 + rise, z1);
          this.b.triFacing(a, bb, c, tileUV(T.brickSoot), this.tint(), this.f.d(sx, 0, 0));
        }
      }
    } else {
      this.flatRoof(baseY + 0.4, 0.6);
    }
    if (this.lot.name) this.signL(this.lot.name, 'painted', 0, baseY - 2.0, D / 2 + 0.03, Math.min(W - 2, 20), false);
    if (this.has('chimney')) {
      const p = this.f.p(-W / 2 + 3, 0, -D / 2 + 3);
      const h = 26;
      this.b.cylinder(p[0], p[2], 0, h, 1.5, 1.0, 12, [0.42, 0.25, 0.2], tileUV(T.brickSoot), { top: false, baseShade: AO.foot });
      this.b.cylinder(p[0], p[2], h - 1.2, h, 1.2, 1.2, 12, [0.15, 0.13, 0.12], WHITE, { topColour: [0.02, 0.02, 0.02] });
      for (const hy of [8, 16]) this.b.cylinder(p[0], p[2], hy, hy + 0.35, 1.5 - (hy / h) * 0.5 + 0.08, 1.5 - (hy / h) * 0.5 + 0.08, 12, C.dark, WHITE, { top: false });
    }
  }

  shed(): void {
    const W = this.W;
    const D = this.D;
    const respray = this.has('respray');
    const rows = [this.H];
    const shedColours: RGB[] = [
      [0.56, 0.44, 0.38],
      [0.4, 0.5, 0.47],
      [0.6, 0.58, 0.54],
    ];
    const colour: RGB = respray ? [0.9, 0.9, 0.9] : shedColours[Math.floor(hash01(this.id, 2) * 3)]!;
    const cols = Math.max(1, Math.round(W / 4.5));
    this.wallGrid([-W / 2, D / 2], [W / 2, D / 2], rows, cols, (c) => (c === Math.floor(cols / 2) ? T.rollerDoor : T.corrugated), colour);
    this.wallGrid([W / 2, D / 2], [W / 2, -D / 2], rows, Math.max(1, Math.round(D / 4.5)), () => T.corrugated, colour);
    this.wallGrid([W / 2, -D / 2], [-W / 2, -D / 2], rows, cols, () => T.corrugated, colour);
    this.wallGrid([-W / 2, -D / 2], [-W / 2, D / 2], rows, Math.max(1, Math.round(D / 4.5)), () => T.corrugated, colour);
    const roofC: RGB = hash01(this.id, 6) < 0.5 ? [0.52, 0.32, 0.23] : [0.42, 0.44, 0.44];
    const axis = W > D ? 'x' : 'z';
    this.gableRoof(this.H, 0.22, axis, T.corrugatedRoof, T.corrugated, 0.4, roofC);
    if (!this.backdrop) {
      // rust has run down from the fixings in streaks, and a ridge ventilator sits on the longer sheds
      const length = axis === 'x' ? W : D;
      // (a thin rust-brown run below a dark fixing head; a wide black bar read as a batten)
      for (let i = 0; i < Math.floor(length / 3); i++) {
        const u = -length / 2 + 1 + i * 3 + hash01(this.id, i, 61) * 1.5;
        const side: 1 | -1 = hash01(this.id, i, 62) < 0.5 ? 1 : -1;
        const top = 0.3 + hash01(this.id, i, 63) * 0.5;
        const w = 0.1 + hash01(this.id, i, 64) * 0.08;
        this.slopePatch(u, u + w, Math.max(0.05, top - 0.2 - hash01(this.id, i, 65) * 0.35), top, side, 0.22, T.white, [0.13, 0.055, 0.022], this.fine, axis);
        this.slopePatch(u - 0.03, u + w + 0.03, top, top + 0.03, side, 0.22, T.white, [0.05, 0.025, 0.012], this.fine, axis);
      }
      if (length > 12) {
        const rise = ((axis === 'x' ? D : W) / 2) * Math.tan(0.22);
        const vl = length * 0.4;
        if (axis === 'x') this.boxL(0, this.H + rise, 0, vl, 0.55, 0.9, [0.3, 0.3, 0.3], T.white, undefined, this.mid);
        else this.boxL(0, this.H + rise, 0, 0.9, 0.55, vl, [0.3, 0.3, 0.3], T.white, undefined, this.mid);
      }
    }
    if (respray) {
      this.boxL(0, this.H - 1.4, D / 2 + 0.12, W - 2, 1.1, 0.2, [0.75, 0.12, 0.1]);
      this.signL("PIKE'S RESPRAY · WHILE-U-WAIT", 'fascia', 0, this.H - 1.35, D / 2 + 0.23, W - 3, true);
      this.glowBox(0, this.H - 1.6, D / 2 + 0.3, W - 3, 0.08, 0.08, [3, 2.4, 1.6]);
    } else if (this.lot.name) {
      this.signL(this.lot.name, 'painted', 0, this.H - 1.6, D / 2 + 0.03, Math.min(W - 2, 14), false);
    }
  }

  lockup(): void {
    const W = this.W;
    const D = this.D;
    const cols = Math.max(1, Math.round(W / 2.8));
    const rows = [this.H];
    const doorTints: RGB[] = [
      [0.8, 0.95, 0.85],
      [0.75, 0.82, 1.0],
      [1.0, 0.8, 0.75],
      [1, 1, 1],
      [0.95, 0.95, 0.8],
    ];
    for (let k = 0; k < cols; k++) {
      const x0 = -W / 2 + (W * k) / cols;
      const x1 = -W / 2 + (W * (k + 1)) / cols;
      this.quadL([x0, D / 2], [x1, D / 2], 0, this.H, T.garageDoor, doorTints[Math.floor(hash01(this.id, k) * doorTints.length)]!);
    }
    const tint = this.tint();
    this.wallGrid([W / 2, D / 2], [W / 2, -D / 2], rows, Math.max(1, Math.round(D / 3)), () => T.brickSoot, tint);
    this.wallGrid([W / 2, -D / 2], [-W / 2, -D / 2], rows, cols, () => T.brickSoot, tint);
    this.wallGrid([-W / 2, -D / 2], [-W / 2, D / 2], rows, Math.max(1, Math.round(D / 3)), () => T.brickSoot, tint);
    this.flatRoof(this.H, 0, T.felt, C.coping, false);
    this.boxL(0, this.H - 0.05, D / 2 + 0.1, W + 0.3, 0.25, 0.4, C.dark);
    if (!this.backdrop) {
      // the felt has been patched in strips of a different roll, and a door or two has a number
      for (let k = 0; k < cols; k++) {
        if (hash01(this.id, k, 64) > 0.3) continue;
        const x0 = -W / 2 + (W * k) / cols + 0.3;
        const x1 = -W / 2 + (W * (k + 1)) / cols - 0.3;
        const tone = hash01(this.id, k, 65) < 0.5 ? 0.7 : 1.2;
        this.fine.quadFacing(this.f.p(x0, this.H + 0.012, D / 2 - 0.3), this.f.p(x1, this.H + 0.012, D / 2 - 0.3), this.f.p(x1, this.H + 0.012, -D / 2 + 0.3), this.f.p(x0, this.H + 0.012, -D / 2 + 0.3), tileUV(T.felt), [0.8 * tone, 0.8 * tone, 0.82 * tone], UP);
      }
    }
  }

  garage(): void {
    const W = this.W;
    const D = this.D;
    const rows = [this.H];
    const cols = Math.max(2, Math.round(W / 5));
    this.wallGrid([-W / 2, D / 2], [W / 2, D / 2], rows, cols, (c) => (c === cols - 1 ? T.factoryWinLit : T.rollerDoor), this.tint());
    this.wallGrid([W / 2, D / 2], [W / 2, -D / 2], rows, Math.max(1, Math.round(D / 4)), () => T.brickSoot, this.tint());
    this.wallGrid([W / 2, -D / 2], [-W / 2, -D / 2], rows, cols, () => T.brickSoot, this.tint());
    this.wallGrid([-W / 2, -D / 2], [-W / 2, D / 2], rows, Math.max(1, Math.round(D / 4)), () => T.brickSoot, this.tint());
    this.flatRoof(this.H, 0.8);
    // big sign board on the roof edge
    this.boxL(0, this.H + 0.8, D / 2 - 0.4, W * 0.8, 1.6, 0.25, [0.1, 0.25, 0.3]);
    this.signL((this.lot.name ?? 'MOTORS').toUpperCase() + ' · REPAIRS · SPARES', 'fascia', 0, this.H + 0.9, D / 2 - 0.26, W * 0.75, true);
    // forecourt pumps
    for (const x of [-3, 3]) {
      this.boxL(x, 0, D / 2 + 4.5, 0.7, 1.7, 0.5, [0.75, 0.1, 0.08]);
      this.glowBox(x, 1.7, D / 2 + 4.5, 0.55, 0.4, 0.4, [3, 2.6, 2]);
    }
    this.boxL(0, 0, D / 2 + 4.5, 9, 0.15, 1.6, C.stone);
  }

  flats(): void {
    const W = this.W;
    const D = this.D;
    const sk = hash01(this.id, 21) < 0.6 ? SKIN.concrete : SKIN.brick;
    const rows = this.floors(this.H, 2.8, 3.0);
    const decks = this.has('deck-access') || hash01(this.id, 5) < 0.5;
    this.walls(rows, 3.2, sk, (col) => (col === 0 ? T.rollerDoor : sk.win[0]!), { backWindows: 0.9, sideWindows: false, courses: sk !== SKIN.concrete });
    // FLATS-BAY / FLATS-DECK (docs/research/BLENDER-BUILDINGS.md P3): on a concrete block the floor slabs are
    // expressed as a band round the back, and the front too when there are no decks (MEDIUM / HIGH)
    if (!this.backdrop && sk === SKIN.concrete) {
      let y = 0;
      for (let r = 1; r < rows.length; r++) {
        y += rows[r - 1]!;
        this.ledge([W / 2, -D / 2], [-W / 2, -D / 2], y - 0.14, 0.28, 0.05, C.stone, this.mid);
        if (!decks) this.ledge([-W / 2, D / 2], [W / 2, D / 2], y - 0.14, 0.28, 0.05, C.stone, this.mid);
      }
    }
    if (decks) {
      const cols = Math.max(1, Math.round(W / 3.2));
      const cellW = W / cols;
      let y = 0;
      for (let r = 0; r < rows.length; r++) {
        if (r > 0) {
          // access decks along the front on every upper floor: the slab, the precast balustrade, its handrail
          this.boxL(0, y - 0.2, D / 2 + 0.75, W, 0.2, 1.5, C.coping);
          const n = Math.max(1, Math.round(W / 4));
          for (let k = 0; k < n; k++) {
            const x0 = -W / 2 + (W * k) / n;
            const x1 = -W / 2 + (W * (k + 1)) / n;
            this.quadL([x0, D / 2 + 1.5], [x1, D / 2 + 1.5], y, y + 1.05, T.deckFront, [1, 1, 1]);
          }
          if (!this.backdrop) this.boxL(0, y + 1.0, D / 2 + 1.47, W, 0.04, 0.05, C.metal, T.white, undefined, this.fine);
        }
        // a front door per flat in the council's bright colours (the window tile leaves the left of the cell
        // plain for it; the ground floor's first cell is the bin store's roller door): the leaf MEDIUM, the
        // dark frame round it HIGH
        if (!this.backdrop) {
          for (let k = r === 0 ? 1 : 0; k < cols; k++) {
            const x0 = -W / 2 + k * cellW + cellW * 0.05;
            const dc = COUNCIL_DOORS[Math.floor(hash01(this.id, k, r + 40) * COUNCIL_DOORS.length)]!;
            this.quadL([x0 - 0.05, D / 2 + 0.008], [x0 + 0.95, D / 2 + 0.008], y, y + 2.1, T.white, [0.2, 0.19, 0.18], 0, this.fine);
            this.quadL([x0, D / 2 + 0.016], [x0 + 0.86, D / 2 + 0.016], y + 0.02, y + 2.05, T.white, dc, 0, this.mid);
          }
        }
        y += rows[r]!;
      }
      // FLATS-TOWER: the stair tower (every tier) with the lift motor room over it (MEDIUM / HIGH) and the
      // louvre on its outer face (HIGH)
      const td = Math.min(D, 6);
      const tx = W / 2 + 1.6;
      this.boxL(tx, 0, 0, 3.2, this.H + 2.2, td, this.tint(), sk.plain, T.felt);
      if (!this.backdrop) {
        this.boxL(tx, this.H + 2.2, 0, 3.4, 0.12, td + 0.2, C.coping, T.white, undefined, this.mid);
        this.boxL(tx, this.H + 2.32, 0, 2.6, 2.4, td - 0.8, this.tint(), T.concretePlain, T.felt, this.mid, AO.roof);
        this.boxL(tx, this.H + 4.72, 0, 2.8, 0.1, td - 0.6, C.coping, T.white, undefined, this.mid);
        for (let i = 0; i < 6; i++) this.boxL(tx + 1.31, this.H + 2.8 + i * 0.28, 0, 0.04, 0.1, td - 2.0, C.dark, T.white, undefined, this.fine);
      }
    }
    if (this.lot.name) this.signL(this.lot.name.toUpperCase(), 'plate', -W / 2 + 3, 2.2, D / 2 + 1.52, 2.6, false);
    this.flatRoof(this.H, 0.6);
    if (!this.backdrop) {
      // FLATS-ROOF: the tank house at the end away from the tower (MEDIUM / HIGH), its door and the forest of
      // TV aerials (HIGH)
      const hd = Math.min(D - 2, 3.0);
      const hx = -W / 2 + 3.2;
      const kit = this.sinks.roofKit;
      if (kit?.house) this.kitModel(kit.house, this.mid, hx, this.H, 0, 3.2, 2.5, hd, 0, this.tint());
      else {
        this.boxL(hx, this.H, 0, 3.2, 2.4, hd, this.tint(), sk.plain, T.felt, this.mid, AO.roof);
        this.boxL(hx, this.H + 2.4, 0, 3.4, 0.1, hd + 0.2, C.coping, T.white, undefined, this.mid);
        this.quadL([hx - 0.45, hd / 2 + 0.012], [hx + 0.45, hd / 2 + 0.012], this.H, this.H + 2.0, T.white, [0.1, 0.22, 0.4], 0, this.fine);
      }
      const na = Math.min(6, Math.max(2, Math.round(W / 9)));
      for (let i = 0; i < na; i++) {
        const ax = -W / 2 + 6 + ((W - 12) * i) / (na - 1);
        const az = (i % 2 ? 1 : -1) * Math.min(2.0, D / 4);
        if (kit?.aerial) this.aerialModel(kit.aerial, ax, this.H, az, 1.1);
        else this.aerial(ax, this.H, az);
      }
    }
  }

  /**
   * A TV aerial on a pole: the pole a quad facing south (the camera always looks north), three crossbars as
   * quads facing up. HIGH only; 8 triangles.
   */
  aerial(lx: number, y: number, lz: number): void {
    const h = 3.6;
    const c = this.f.p(lx, y, lz);
    const b = this.fine;
    b.quadFacing([c[0] - 0.03, y, c[2]], [c[0] + 0.03, y, c[2]], [c[0] + 0.03, y + h, c[2]], [c[0] - 0.03, y + h, c[2]], WHITE, C.metal, [0, 0, 1]);
    for (let j = 0; j < 3; j++) {
      const l = 0.8 - j * 0.15;
      const yy = y + h - 0.1 - j * 0.35;
      b.quadFacing([c[0] - l, yy, c[2] - 0.03], [c[0] + l, yy, c[2] - 0.03], [c[0] + l, yy, c[2] + 0.03], [c[0] - l, yy, c[2] + 0.03], WHITE, C.metal, UP);
    }
  }

  office(): void {
    const sk = this.skin();
    const rows = this.floors(this.H, 3.2, 3.6);
    const doorCol = Math.floor(hash01(this.id, 3) * 2);
    this.walls(rows, 3.4, sk, (col) => (col === doorCol ? sk.door : sk.win[0]!));
    // cornice: the main slab, a second step above it and a dentil course below (HIGH)
    this.boxL(0, this.H - 0.3, 0, this.W + 0.4, 0.3, this.D + 0.4, C.cornice);
    this.eaveFaces.add('front').add('back').add('side');
    if (!this.backdrop) {
      this.ledge([-this.W / 2, this.D / 2], [this.W / 2, this.D / 2], this.H - 0.52, 0.22, 0.26, C.cornice, this.mid);
      this.dentils(this.H - 0.7);
    }
    if (this.lot.name) this.signL(this.lot.name, 'fascia', -this.W / 2 + 3.4 * (doorCol + 0.5), 2.7, this.D / 2 + 0.05, 3.2, false);
    if (hash01(this.id, 44) < 0.3 && this.D < 16) {
      const rise = this.gableRoof(this.H, 0.5, 'x');
      this.chimneyStack(this.W / 4, 0, this.H + rise - 0.4, 0.8, 1.3, 1.6, 2, 15);
    } else this.flatRoof(this.H, 0.9);
  }

  bank(): void {
    const W = this.W;
    const D = this.D;
    const rows = [4.4, ...this.floors(this.H - 4.4, 3.6)];
    const cols = Math.max(3, Math.round(W / 3.6));
    const mid = Math.floor(cols / 2);
    this.walls(rows, W / cols, SKIN.stone, (col) => (col === mid ? T.stoneDoor : T.stoneWinLit), { sideWindows: true });
    // portico: steps, columns, entablature, pediment
    const pw = Math.min(W - 1, 8);
    this.boxL(0, 0, D / 2 + 1.6, pw + 1.2, 0.3, 3.2, C.stone);
    this.boxL(0, 0.3, D / 2 + 1.3, pw + 0.6, 0.3, 2.6, C.stone);
    for (let i = 0; i < 4; i++) {
      const x = -pw / 2 + 0.5 + (i * (pw - 1)) / 3;
      const p = this.f.p(x, 0, D / 2 + 1.9);
      this.b.cylinder(p[0], p[2], 0.6, 5.4, 0.36, 0.3, 10, [0.78, 0.76, 0.7], WHITE, { top: false, baseShade: AO.recess });
    }
    this.boxL(0, 5.4, D / 2 + 1.3, pw + 0.4, 0.9, 2.6, [0.74, 0.72, 0.66]);
    const a = this.f.p(-pw / 2 - 0.2, 6.3, D / 2 + 2.6);
    const bb = this.f.p(pw / 2 + 0.2, 6.3, D / 2 + 2.6);
    const c = this.f.p(0, 7.8, D / 2 + 2.6);
    this.b.triFacing(a, bb, c, tileUV(T.stonePlain), [1, 1, 1], this.f.d(0, 0, 1));
    const a2 = this.f.p(-pw / 2 - 0.2, 6.3, D / 2);
    const b2 = this.f.p(pw / 2 + 0.2, 6.3, D / 2);
    const c2 = this.f.p(0, 7.8, D / 2);
    this.b.quadFacing(a2, a, c, c2, tileUV(T.lead), C.lead, UP);
    this.b.quadFacing(bb, b2, c2, c, tileUV(T.lead), C.lead, UP);
    if (this.lot.name) this.signL(this.lot.name.toUpperCase(), 'painted', 0, 5.5, D / 2 + 2.62, pw - 0.4, false);
    this.boxL(0, this.H - 0.4, 0, W + 0.5, 0.4, D + 0.5, C.cornice);
    this.eaveFaces.add('front').add('back').add('side');
    if (!this.backdrop) {
      this.ledge([-W / 2, D / 2], [W / 2, D / 2], this.H - 0.66, 0.26, 0.3, C.cornice, this.mid);
      this.dentils(this.H - 0.86);
      // rusticated ground floor: a deep joint line every course
      for (let y = 0.9; y < 4.2; y += 0.75) this.ledge([-W / 2, D / 2], [W / 2, D / 2], y, 0.05, 0.04, [0.4, 0.39, 0.36], this.fine);
    }
    this.flatRoof(this.H, 1.0, T.lead);
  }

  station(): void {
    const W = this.W;
    const D = this.D;
    const rows = this.floors(this.H, 3.3, 3.8);
    const cols = Math.max(3, Math.round(W / 3.4));
    const mid = Math.floor(cols / 2);
    this.walls(rows, W / cols, SKIN.brick, (col) => (col === mid ? T.stoneDoor : T.brickWinLit), { sideWindows: true });
    this.boxL(0, 0, D / 2 + 0.8, 3.4, 0.3, 1.6, C.stone);
    // blue lamp on a bracket
    this.boxL(0, 3.3, D / 2 + 0.5, 0.1, 0.1, 1.0, C.dark);
    this.glowBox(0, 3.0, D / 2 + 1.0, 0.45, 0.6, 0.45, [0.5, 1.1, 4.0]);
    this.signL('POLICE', 'police', 0, 4.0, D / 2 + 0.05, 2.6, true);
    if (this.lot.name) this.signL(this.lot.name.toUpperCase(), 'painted', 0, this.H - 1.6, D / 2 + 0.05, Math.min(W - 2, 16), false);
    this.boxL(0, this.H - 0.3, 0, W + 0.3, 0.3, D + 0.3, C.cornice);
    this.eaveFaces.add('front').add('back').add('side');
    if (!this.backdrop) this.ledge([-W / 2, D / 2], [W / 2, D / 2], this.H - 0.5, 0.2, 0.24, C.cornice, this.mid);
    this.flatRoof(this.H, 0.9);
    // radio mast
    const p = this.f.p(W / 2 - 2, 0, -D / 2 + 2);
    this.b.cylinder(p[0], p[2], this.H, this.H + 9, 0.12, 0.05, 5, C.metal, WHITE);
  }

  churchNave(): void {
    const W = this.W; // across the nave
    const D = this.D; // nave length (local Z)
    const wallH = 7.5;
    const tint: RGB = [0.95, 0.93, 0.9];
    const bays = Math.max(3, Math.round(D / 4.5));
    // long side walls with lancets
    this.wallGrid([W / 2, D / 2], [W / 2, -D / 2], [wallH], bays, () => T.lancetLit, tint);
    this.wallGrid([-W / 2, -D / 2], [-W / 2, D / 2], [wallH], bays, () => T.lancetLit, tint);
    // west front (local +Z faces the square) & east end
    this.wallGrid([-W / 2, D / 2], [W / 2, D / 2], [wallH], 3, (c) => (c === 1 ? T.stoneDoor : T.stonePlain), tint);
    this.wallGrid([W / 2, -D / 2], [-W / 2, -D / 2], [wallH], 3, (c) => (c === 1 ? T.lancetLit : T.stonePlain), tint);
    // buttresses
    for (let i = 0; i <= bays; i++) {
      const z = -D / 2 + (D * i) / bays;
      for (const sx of [-1, 1]) this.boxL((sx * (W + 1.2)) / 2, 0, z, 0.9, wallH - 1, 0.8, C.stone, T.stonePlain);
    }
    this.gableRoof(wallH, 0.95, 'z', T.slate, T.stonePlain, 0.4);
    // rose window glow on the east gable
    const p = this.f.p(0, wallH + 2.2, -D / 2 - 0.05);
    this.glow.cylinder(p[0], p[2], p[1] - 0.05, p[1] + 0.05, 1.1, 1.1, 12, [2.6, 1.3, 0.6], WHITE);
    // chancel (lower, narrower) at the east end
    this.boxL(0, 0, -D / 2 - 3, W * 0.65, 6, 6, tint, T.lancetLit, T.slate);
  }

  churchTower(): void {
    const s = this.W;
    const towerH = 22;
    const tint: RGB = [0.95, 0.93, 0.9];
    const rows = [6, 6, 5, 5];
    const faces: [[number, number], [number, number]][] = [
      [
        [-s / 2, s / 2],
        [s / 2, s / 2],
      ],
      [
        [s / 2, s / 2],
        [s / 2, -s / 2],
      ],
      [
        [s / 2, -s / 2],
        [-s / 2, -s / 2],
      ],
      [
        [-s / 2, -s / 2],
        [-s / 2, s / 2],
      ],
    ];
    faces.forEach(([a, b], i) => this.wallGrid(a, b, rows, 2, (c, r) => (r === 0 && i === 0 && c === 0 ? T.stoneDoor : r === 3 ? T.lancetLit : T.stonePlain), tint));
    // corner buttresses and pinnacles
    for (const [x, z] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      this.boxL((x * s) / 2, 0, (z * s) / 2, 1.1, towerH - 1, 1.1, C.stone, T.stonePlain);
      const p = this.f.p((x * s) / 2, 0, (z * s) / 2);
      this.b.cylinder(p[0], p[2], towerH, towerH + 2.6, 0.45, 0.02, 4, C.stone, WHITE, { phase: Math.PI / 4 });
    }
    // parapet
    this.boxL(0, towerH, 0, s + 0.6, 0.8, s + 0.6, C.stone, T.stonePlain);
    // clock faces (glow) on the front
    const cp = this.f.p(0, 15.5, s / 2 + 0.06);
    const cfx = cp[0];
    const cfz = cp[2];
    const n = 16;
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * Math.PI * 2;
      const a1 = ((i + 1) / n) * Math.PI * 2;
      const r = 1.2;
      const ax = Math.cos(this.lot.angle);
      const az = -Math.sin(this.lot.angle);
      this.glow.triFacing(
        [cfx, cp[1], cfz],
        [cfx + ax * Math.cos(a0) * r, cp[1] + Math.sin(a0) * r, cfz + az * Math.cos(a0) * r],
        [cfx + ax * Math.cos(a1) * r, cp[1] + Math.sin(a1) * r, cfz + az * Math.cos(a1) * r],
        WHITE,
        [2.4, 2.2, 1.7],
        this.f.d(0, 0, 1),
      );
    }
    // spire
    const sp = this.f.p(0, 0, 0);
    this.b.cylinder(sp[0], sp[2], towerH + 0.8, this.H, s * 0.42, 0.05, 8, [0.42, 0.45, 0.48], tileUV(T.slate), { phase: Math.PI / 8 });
    this.boxL(0, this.H - 0.2, 0, 0.08, 1.6, 0.08, C.gold);
  }
}
